/**
 * Universal Web Tracking SDK v1.0
 * Automatically tracks: Clicks, Heatmaps, Products, Sessions, Browser Data, IP, Events
 * Can be added to ANY web application
 */

(function(window, document) {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://cvm-api.worktual.co.uk/cvm_api_service/v1/insert_tracking_nfspath',
    domainId: 20556,
    domainName: window.location.hostname,
    autoTrack: true,
    trackProducts: true,
    trackHeatmap: true,
    trackSession: true,
    batchInterval: 30000, // Send metrics every 30 seconds
    debug: false,
  };

  // Tracking state
  const STATE = {
    visitorId: null,
    sessionId: null,
    pageLoadTime: Date.now(),
    customerDetails: {
      email: null,
      mobile: null,
      name: null,
      firstName: null,
      lastName: null,
      customerId: null,
      address: null,
      city: null,
      country: null,
      ordersCount: null,
      totalSpent: null,
      tags: null,
      acceptsMarketing: null
    },
    // Shopify Cart State
    cart: {
      token: null,
      items: [],
      itemCount: 0,
      totalPrice: 0,
      currency: null // Will be set dynamically from Shopify
    },
    metrics: {
      Heap_Tracking: { name: 'Heap_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Heatmap_Tracking: { name: 'Heatmap_Tracking', status: 'success', data: { clicks: [], moves: [], scrolls: [], rageClicks: [], deadClicks: [] }, lastUpdate: 0, count: 0 },
      Product_Click_Tracking: { name: 'Product_Click_Tracking', status: 'success', data: { clicks: [] }, metadata: {}, lastUpdate: 0, count: 0 },
      Visitor_ID: { name: 'Visitor_ID', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Browser_Fingerprint: { name: 'Browser_Fingerprint', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Referrer_Info: { name: 'Referrer_Info', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      IP_Tracking: { name: 'IP_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Event_Tracking: { name: 'Event_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Pixel_Tracking: { name: 'Pixel_Tracking', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      Cookie_Operations: { name: 'Cookie_Operations', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      // NEW: Shopify Cart Tracking
      Cart_Tracking: { name: 'Cart_Tracking', status: 'success', data: { events: [], currentCart: null }, lastUpdate: 0, count: 0 },
      // NEW: Page Performance Tracking
      Page_Performance: { name: 'Page_Performance', status: 'success', data: {}, lastUpdate: 0, count: 0 },
      // NEW: Error Tracking
      Error_Tracking: { name: 'Error_Tracking', status: 'success', data: { errors: [] }, lastUpdate: 0, count: 0 },
      // NEW: Network Request Tracking
      Network_Tracking: { name: 'Network_Tracking', status: 'success', data: { requests: [], failedRequests: [] }, lastUpdate: 0, count: 0 },
      // NEW: User Authentication Tracking
      User_Auth_Tracking: { name: 'User_Auth_Tracking', status: 'success', data: { events: [], signInCaptures: [], currentSignInState: null }, lastUpdate: 0, count: 0 },
      // NEW: Order/Purchase Tracking
      Order_Tracking: { name: 'Order_Tracking', status: 'success', data: { orders: [], checkoutSteps: [] }, lastUpdate: 0, count: 0 },
      // NEW: Wishlist Tracking
      Wishlist_Tracking: { name: 'Wishlist_Tracking', status: 'success', data: { items: [], events: [] }, lastUpdate: 0, count: 0 },
      // NEW: Product Review Tracking
      Review_Tracking: { name: 'Review_Tracking', status: 'success', data: { reviews: [], events: [] }, lastUpdate: 0, count: 0 },
      // NEW: Search Tracking
      Search_Tracking: { name: 'Search_Tracking', status: 'success', data: { searches: [] }, lastUpdate: 0, count: 0 },
      // NEW: Coupon/Discount Tracking
      Coupon_Tracking: { name: 'Coupon_Tracking', status: 'success', data: { coupons: [] }, lastUpdate: 0, count: 0 },
      // NEW: Checkout Contact Information Metrics
      Checkout_Contact_Metrics: { name: 'Checkout_Contact_Metrics', status: 'success', data: { steps: [], fieldInteractions: [], formCompletions: [] }, lastUpdate: 0, count: 0 },
    },
    batchTimer: null,
    // Rage click detection state
    clickHistory: [],
    lastClickElement: null,
    // Original fetch reference for interception
    originalFetch: null,
    originalXHROpen: null,
    originalXHRSend: null,
    // Checkout contact info step tracking state
    checkoutContactInfo: {
      stepStartTime: null,
      currentStep: null,
      fieldInteractions: {},
      activeField: null,
      activeFieldStartTime: null,
      stepObserver: null,
      formFieldsTracked: false
    },
  };

  // Utility: Generate unique ID
  function generateId(prefix) {
    return prefix + '_' + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
  }

  // Utility: Get/Set Cookie
  function getCookie(name) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const cookie = cookies.find(row => row.startsWith(name + '='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expires + '; SameSite=Lax';
  }

  // Utility: Check consent
  function isConsentMissing() {
    const consent = getCookie('CK_Cons') || localStorage.getItem('cookieConsent');
    return !consent;
  }

  // Utility: Get Shop Currency (dynamically from Shopify)
  function getShopCurrency() {
    // Method 1: Shopify global object
    if (window.Shopify && window.Shopify.currency) {
      return window.Shopify.currency.active || window.Shopify.currency.rate;
    }

    // Method 2: Shopify checkout object
    if (window.Shopify && window.Shopify.checkout && window.Shopify.checkout.currency) {
      return window.Shopify.checkout.currency;
    }

    // Method 3: ShopifyAnalytics meta
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.currency) {
      return window.ShopifyAnalytics.meta.currency;
    }

    // Method 4: Meta tag
    const currencyMeta = document.querySelector('meta[name="shopify-currency"], meta[property="og:price:currency"]');
    if (currencyMeta) {
      return currencyMeta.getAttribute('content');
    }

    // Method 5: Data attribute on body or html
    const currencyAttr = document.body.getAttribute('data-currency') ||
                         document.documentElement.getAttribute('data-currency') ||
                         document.body.getAttribute('data-shop-currency');
    if (currencyAttr) {
      return currencyAttr;
    }

    // Method 6: Parse from price elements on page
    const priceElement = document.querySelector('[data-currency], .money, .price');
    if (priceElement) {
      const currencyData = priceElement.getAttribute('data-currency');
      if (currencyData) return currencyData;

      // Try to detect from price text (e.g., "$", "€", "£", "₹")
      const priceText = priceElement.textContent || '';
      if (priceText.includes('$') && !priceText.includes('CA$') && !priceText.includes('AU$')) return 'USD';
      if (priceText.includes('€')) return 'EUR';
      if (priceText.includes('£')) return 'GBP';
      if (priceText.includes('₹')) return 'INR';
      if (priceText.includes('¥')) return 'JPY';
      if (priceText.includes('CA$')) return 'CAD';
      if (priceText.includes('AU$')) return 'AUD';
      if (priceText.includes('₩')) return 'KRW';
      if (priceText.includes('CHF')) return 'CHF';
      if (priceText.includes('AED')) return 'AED';
      if (priceText.includes('SAR')) return 'SAR';
    }

    // Method 7: From Shopify Liquid injected variable
    if (window.__SHOPIFY_CURRENCY__) {
      return window.__SHOPIFY_CURRENCY__;
    }

    // Method 8: Check locale/country for default currency
    const lang = navigator.language || navigator.userLanguage;
    const countryCode = lang.split('-')[1]?.toUpperCase();
    const countryCurrencyMap = {
      'US': 'USD', 'GB': 'GBP', 'UK': 'GBP', 'EU': 'EUR', 'DE': 'EUR', 'FR': 'EUR',
      'IT': 'EUR', 'ES': 'EUR', 'IN': 'INR', 'JP': 'JPY', 'CN': 'CNY', 'KR': 'KRW',
      'AU': 'AUD', 'CA': 'CAD', 'AE': 'AED', 'SA': 'SAR', 'CH': 'CHF', 'SG': 'SGD',
      'MY': 'MYR', 'TH': 'THB', 'PH': 'PHP', 'ID': 'IDR', 'VN': 'VND', 'BR': 'BRL',
      'MX': 'MXN', 'ZA': 'ZAR', 'NZ': 'NZD', 'HK': 'HKD', 'TW': 'TWD', 'RU': 'RUB'
    };
    if (countryCode && countryCurrencyMap[countryCode]) {
      return countryCurrencyMap[countryCode];
    }

    // Default fallback
    return 'USD';
  }

  // Initialize Shop Currency
  function initShopCurrency() {
    const currency = getShopCurrency();
    STATE.cart.currency = currency;
    if (CONFIG.debug) console.log('Shop currency detected:', currency);
    return currency;
  }

  // Initialize Visitor ID
  function initVisitorId() {
    let visitorId = getCookie('visitor_id');
    if (!visitorId) {
      visitorId = generateId('visitor');
      setCookie('visitor_id', visitorId, 365);
      trackCookieOperation('set', 'visitor_id', visitorId, 365);
    } else {
      trackCookieOperation('get', 'visitor_id', visitorId);
    }
    STATE.visitorId = visitorId;

    STATE.metrics.Visitor_ID.data = {
      visitorId: visitorId,
      timestamp: Date.now()
    };
    STATE.metrics.Visitor_ID.lastUpdate = Date.now();
    STATE.metrics.Visitor_ID.count = 1;
  }

  // Initialize Session ID
  function initSessionId() {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = generateId('session');
      sessionStorage.setItem('session_id', sessionId);
    }
    STATE.sessionId = sessionId;
  }

  // Initialize Customer Details - Shopify Integration
  function initCustomerDetails() {
    // Try to get customer data from Shopify's global objects
    const customer = getShopifyCustomer();
    if (customer) {
      updateCustomerDetails(customer);
    }

    // Also check for stored customer data
    const storedCustomer = localStorage.getItem('wt_customer_details');
    if (storedCustomer && !STATE.customerDetails.email) {
      try {
        const parsed = JSON.parse(storedCustomer);
        updateCustomerDetails(parsed);
      } catch (e) {
        if (CONFIG.debug) console.warn('Failed to parse stored customer data:', e);
      }
    }

    // Listen for Shopify customer login events
    listenForCustomerChanges();
  }

  // Get Shopify Customer Data
  function getShopifyCustomer() {
    const customer = {};

    // Method 1: Shopify's __st object (Shopify Analytics)
    if (window.__st && window.__st.cid) {
      customer.customerId = window.__st.cid;
    }

    // Method 2: Shopify's ShopifyAnalytics object
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
      const meta = window.ShopifyAnalytics.meta;
      if (meta.page.customerId) {
        customer.customerId = meta.page.customerId;
      }
    }

    // Method 3: Shopify's meta tags
    const customerIdMeta = document.querySelector('meta[name="shopify-customer-id"]');
    if (customerIdMeta) {
      customer.customerId = customerIdMeta.getAttribute('content');
    }

    // Method 4: Check Shopify's window.customer object (if available via theme)
    if (window.customer) {
      if (window.customer.email) customer.email = window.customer.email;
      if (window.customer.phone) customer.mobile = window.customer.phone;
      if (window.customer.first_name) customer.firstName = window.customer.first_name;
      if (window.customer.last_name) customer.lastName = window.customer.last_name;
      if (window.customer.name) customer.name = window.customer.name;
      if (window.customer.id) customer.customerId = window.customer.id;
    }

    // Method 5: Check for Shopify Liquid-injected customer data
    if (window.__SHOPIFY_CUSTOMER__) {
      const sc = window.__SHOPIFY_CUSTOMER__;
      if (sc.email) customer.email = sc.email;
      if (sc.phone) customer.mobile = sc.phone;
      if (sc.firstName || sc.first_name) customer.firstName = sc.firstName || sc.first_name;
      if (sc.lastName || sc.last_name) customer.lastName = sc.lastName || sc.last_name;
      if (sc.id) customer.customerId = sc.id;
      if (sc.ordersCount || sc.orders_count) customer.ordersCount = sc.ordersCount || sc.orders_count;
      if (sc.totalSpent || sc.total_spent) customer.totalSpent = sc.totalSpent || sc.total_spent;
      if (sc.tags) customer.tags = sc.tags;
      if (sc.acceptsMarketing !== undefined && sc.acceptsMarketing !== null) customer.acceptsMarketing = sc.acceptsMarketing;
    }

    // Method 6: Parse customer data from data attributes on body or specific elements
    const customerDataEl = document.querySelector('[data-customer-email]');
    if (customerDataEl) {
      customer.email = customerDataEl.getAttribute('data-customer-email') || customer.email;
      customer.mobile = customerDataEl.getAttribute('data-customer-phone') || customer.mobile;
      customer.firstName = customerDataEl.getAttribute('data-customer-first-name') || customer.firstName;
      customer.lastName = customerDataEl.getAttribute('data-customer-last-name') || customer.lastName;
    }

    // Build full name if we have first/last but no full name
    if (!customer.name && (customer.firstName || customer.lastName)) {
      customer.name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    }

    return Object.keys(customer).length > 0 ? customer : null;
  }

  // Update Customer Details
  function updateCustomerDetails(details) {
    if (!details) return;

    if (details.email) STATE.customerDetails.email = details.email;
    if (details.mobile || details.phone) STATE.customerDetails.mobile = details.mobile || details.phone;
    if (details.name) STATE.customerDetails.name = details.name;
    if (details.firstName || details.first_name) STATE.customerDetails.firstName = details.firstName || details.first_name;
    if (details.lastName || details.last_name) STATE.customerDetails.lastName = details.lastName || details.last_name;
    if (details.customerId || details.id) STATE.customerDetails.customerId = details.customerId || details.id;
    if (details.address) STATE.customerDetails.address = details.address;
    if (details.city) STATE.customerDetails.city = details.city;
    if (details.country) STATE.customerDetails.country = details.country;
    if (details.ordersCount || details.orders_count) STATE.customerDetails.ordersCount = details.ordersCount || details.orders_count;
    if (details.totalSpent || details.total_spent) STATE.customerDetails.totalSpent = details.totalSpent || details.total_spent;
    if (details.tags) STATE.customerDetails.tags = details.tags;
    if (details.acceptsMarketing !== undefined && details.acceptsMarketing !== null) STATE.customerDetails.acceptsMarketing = details.acceptsMarketing;

    // Build full name if not provided
    if (!STATE.customerDetails.name && (STATE.customerDetails.firstName || STATE.customerDetails.lastName)) {
      STATE.customerDetails.name = [STATE.customerDetails.firstName, STATE.customerDetails.lastName].filter(Boolean).join(' ');
    }

    // Persist customer details
    localStorage.setItem('wt_customer_details', JSON.stringify(STATE.customerDetails));

    if (CONFIG.debug) console.log('Customer details updated:', STATE.customerDetails);
  }

  // Listen for customer changes (login/logout, form submissions)
  function listenForCustomerChanges() {
    // Monitor for Shopify customer login via page reload detection
    const checkInterval = setInterval(() => {
      const customer = getShopifyCustomer();
      if (customer && customer.email && !STATE.customerDetails.email) {
        updateCustomerDetails(customer);

        // Capture sign-in identity when customer detected via polling
        captureSignInIdentity({
          email: customer.email,
          firstName: customer.firstName,
          lastName: customer.lastName,
          phone: customer.mobile,
          customerId: customer.customerId,
          ordersCount: customer.ordersCount,
          totalSpent: customer.totalSpent,
          tags: customer.tags,
          acceptsMarketing: customer.acceptsMarketing,
          method: 'shopify_session',
          source: 'polling_detection'
        });

        clearInterval(checkInterval);
      }
    }, 2000);

    // Stop checking after 30 seconds
    setTimeout(() => clearInterval(checkInterval), 30000);

    // Capture customer details from form submissions
    document.addEventListener('submit', function(e) {
      const form = e.target;
      captureFormCustomerDetails(form);
    });

    // Capture from input blur events on email/phone fields
    document.addEventListener('blur', function(e) {
      const target = e.target;
      if (target.tagName === 'INPUT') {
        captureInputCustomerDetails(target);
      }
    }, true);
  }

  // Capture customer details from forms
  function captureFormCustomerDetails(form) {
    const formData = {};

    // Email fields
    const emailInput = form.querySelector('input[type="email"], input[name*="email"], input[id*="email"]');
    if (emailInput && emailInput.value) {
      formData.email = emailInput.value;
    }

    // Phone fields
    const phoneInput = form.querySelector('input[type="tel"], input[name*="phone"], input[name*="mobile"], input[id*="phone"], input[id*="mobile"]');
    if (phoneInput && phoneInput.value) {
      formData.mobile = phoneInput.value;
    }

    // Name fields
    const firstNameInput = form.querySelector('input[name*="first_name"], input[name*="firstName"], input[id*="first_name"], input[id*="firstName"]');
    const lastNameInput = form.querySelector('input[name*="last_name"], input[name*="lastName"], input[id*="last_name"], input[id*="lastName"]');
    const nameInput = form.querySelector('input[name*="name"]:not([name*="first"]):not([name*="last"]), input[id*="name"]:not([id*="first"]):not([id*="last"])');

    if (firstNameInput && firstNameInput.value) formData.firstName = firstNameInput.value;
    if (lastNameInput && lastNameInput.value) formData.lastName = lastNameInput.value;
    if (nameInput && nameInput.value && !formData.firstName) formData.name = nameInput.value;

    if (Object.keys(formData).length > 0) {
      updateCustomerDetails(formData);

      // Capture sign-in identity from form submission
      if (formData.email || formData.mobile) {
        captureSignInIdentity({
          email: formData.email || null,
          firstName: formData.firstName || null,
          lastName: formData.lastName || null,
          phone: formData.mobile || null,
          name: formData.name || null,
          method: 'form_submission',
          source: 'form_capture',
          formId: form.id || form.getAttribute('action') || null
        });
      }
    }
  }

  // Capture customer details from individual input fields
  function captureInputCustomerDetails(input) {
    const name = (input.name || '').toLowerCase();
    const id = (input.id || '').toLowerCase();
    const type = input.type;
    const value = input.value;

    if (!value) return;

    const formData = {};

    // Email detection
    if (type === 'email' || name.includes('email') || id.includes('email')) {
      if (isValidEmail(value)) {
        formData.email = value;
      }
    }

    // Phone detection
    if (type === 'tel' || name.includes('phone') || name.includes('mobile') || id.includes('phone') || id.includes('mobile')) {
      formData.mobile = value;
    }

    // Name detection
    if (name.includes('first_name') || name.includes('firstname') || id.includes('first_name') || id.includes('firstname')) {
      formData.firstName = value;
    }
    if (name.includes('last_name') || name.includes('lastname') || id.includes('last_name') || id.includes('lastname')) {
      formData.lastName = value;
    }

    if (Object.keys(formData).length > 0) {
      updateCustomerDetails(formData);

      // Capture sign-in identity from input blur (email/phone field completed)
      if (formData.email || formData.mobile) {
        captureSignInIdentity({
          email: formData.email || null,
          firstName: formData.firstName || null,
          lastName: formData.lastName || null,
          phone: formData.mobile || null,
          method: 'input_capture',
          source: 'field_blur'
        });
      }
    }
  }

  // Validate email format
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Get current customer details (for inclusion in events)
  function getCustomerDetails() {
    const details = {};
    if (STATE.customerDetails.email) details.email = STATE.customerDetails.email;
    if (STATE.customerDetails.mobile) details.mobile = STATE.customerDetails.mobile;
    if (STATE.customerDetails.name) details.name = STATE.customerDetails.name;
    if (STATE.customerDetails.firstName) details.firstName = STATE.customerDetails.firstName;
    if (STATE.customerDetails.lastName) details.lastName = STATE.customerDetails.lastName;
    if (STATE.customerDetails.customerId) details.customerId = STATE.customerDetails.customerId;
    if (STATE.customerDetails.address) details.address = STATE.customerDetails.address;
    if (STATE.customerDetails.city) details.city = STATE.customerDetails.city;
    if (STATE.customerDetails.country) details.country = STATE.customerDetails.country;
    if (STATE.customerDetails.ordersCount) details.ordersCount = STATE.customerDetails.ordersCount;
    if (STATE.customerDetails.totalSpent) details.totalSpent = STATE.customerDetails.totalSpent;
    if (STATE.customerDetails.tags) details.tags = STATE.customerDetails.tags;
    if (STATE.customerDetails.acceptsMarketing !== null && STATE.customerDetails.acceptsMarketing !== undefined) details.acceptsMarketing = STATE.customerDetails.acceptsMarketing;
    return Object.keys(details).length > 0 ? details : null;
  }

  // Browser Fingerprinting
  function getBrowserFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }

    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      canvas: canvas.toDataURL(),
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      indexedDB: !!window.indexedDB,
    };

    // Add connection info if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      fingerprint.connection = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      };
    }

    STATE.metrics.Browser_Fingerprint.data = fingerprint;
    STATE.metrics.Browser_Fingerprint.lastUpdate = Date.now();
    STATE.metrics.Browser_Fingerprint.count = 1;

    return fingerprint;
  }

  // Get Referrer Info
  function getReferrerInfo() {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) utmParams[param] = value;
    });

    const info = {
      referrer: referrer || 'direct',
      currentUrl: window.location.href,
      utmParams: utmParams,
      searchParams: Object.fromEntries(urlParams.entries()),
      isExternalReferrer: referrer && !referrer.includes(window.location.hostname),
      referrerDomain: referrer ? new URL(referrer).hostname : null
    };

    STATE.metrics.Referrer_Info.data = info;
    STATE.metrics.Referrer_Info.lastUpdate = Date.now();
    STATE.metrics.Referrer_Info.count = 1;

    return info;
  }

  // Track IP Address with Geolocation
  async function trackIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      const ip = data.ip;

      // Try to get location data
      try {
        const locationResponse = await fetch(`https://ipapi.co/${ip}/json/`);
        const locationData = await locationResponse.json();

        STATE.metrics.IP_Tracking.data = {
          ip: ip,
          timestamp: Date.now(),
          location: {
            city: locationData.city,
            region: locationData.region,
            country: locationData.country_name,
            countryCode: locationData.country_code,
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            timezone: locationData.timezone,
            isp: locationData.org
          }
        };
      } catch (locError) {
        // Fallback without location
        STATE.metrics.IP_Tracking.data = {
          ip: ip,
          timestamp: Date.now()
        };
      }

      STATE.metrics.IP_Tracking.lastUpdate = Date.now();
      STATE.metrics.IP_Tracking.count = 1;
    } catch (error) {
      if (CONFIG.debug) console.warn('IP tracking failed:', error);
    }
  }

  // Product Click Tracking
  function trackProductClick(event) {
    const target = event.target;
    const productElement = target.closest('[data-product-id]');

    if (!productElement) return;

    const productId = productElement.getAttribute('data-product-id') || '';
    const productData = {
      productId: productId,
      productName: productElement.getAttribute('data-product-name') || '',
      productCategory: productElement.getAttribute('data-product-category') || '',
      productPrice: parseFloat(productElement.getAttribute('data-product-price') || '0'),
      productCurrency: productElement.getAttribute('data-product-currency') || 'USD',
      productSKU: productElement.getAttribute('data-product-sku') || undefined,
      productBrand: productElement.getAttribute('data-product-brand') || undefined,
      productImage: productElement.querySelector('img')?.src || '',
      productUrl: window.location.href,
      productAvailability: productElement.getAttribute('data-product-availability') || 'in_stock',

      clickPosition: { x: event.clientX, y: event.clientY },
      elementClicked: getElementType(target),
      section: productElement.getAttribute('data-section') || productElement.getAttribute('data-track-section') || 'unknown',
      pageContext: window.location.pathname,

      timestamp: Date.now(),
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      timeOnPage: Date.now() - STATE.pageLoadTime,
      scrollDepth: getScrollDepth(),
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Product_Click_Tracking.data.clicks.push(productData);
    STATE.metrics.Product_Click_Tracking.lastUpdate = Date.now();
    STATE.metrics.Product_Click_Tracking.count = STATE.metrics.Product_Click_Tracking.data.clicks.length;
    STATE.metrics.Product_Click_Tracking.metadata = {
      pageType: getPageType(),
      totalProductsViewed: STATE.metrics.Product_Click_Tracking.data.clicks.length
    };

    // Track as pixel/conversion event
    trackPixelEvent('product_click', productId);

    // Track as custom event
    if (!STATE.metrics.Event_Tracking.data.events) {
      STATE.metrics.Event_Tracking.data.events = [];
    }
    STATE.metrics.Event_Tracking.data.events.push({
      action: 'product_clicked',
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      data: {
        productId: productId,
        productName: productData.productName,
        section: productData.section
      }
    });
    STATE.metrics.Event_Tracking.lastUpdate = Date.now();
    STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;

    if (CONFIG.debug) console.log('Product click tracked:', productData);
  }

  // Heatmap Tracking
  function trackClick(event) {
    const clickData = {
      x: event.clientX,
      y: event.clientY,
      type: 'click',
      timestamp: Date.now(),
      element: getElementSelector(event.target),
      elementType: event.target.tagName.toLowerCase(),
      pageX: event.pageX,
      pageY: event.pageY,
      page: window.location.pathname,
      url: window.location.href,
      screenWidth: screen.width,
      screenHeight: screen.height,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Heatmap_Tracking.data.clicks.push(clickData);
    STATE.metrics.Heatmap_Tracking.lastUpdate = Date.now();
    STATE.metrics.Heatmap_Tracking.count = STATE.metrics.Heatmap_Tracking.data.clicks.length;
  }

  // Track Mouse Movement (sampled)
  let mouseMoveThrottle = 0;
  function trackMouseMove(event) {
    const now = Date.now();
    if (now - mouseMoveThrottle < 200) return; // Throttle to every 200ms
    mouseMoveThrottle = now;

    const moveData = {
      x: event.clientX,
      y: event.clientY,
      type: 'move',
      timestamp: now,
      element: getElementSelector(event.target),
      elementType: event.target.tagName.toLowerCase(),
      value: Math.random() * 100, // Intensity value
      pageX: event.pageX,
      pageY: event.pageY,
      page: window.location.pathname,
      url: window.location.href,
      screenWidth: screen.width,
      screenHeight: screen.height,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      sessionId: STATE.sessionId
    };

    STATE.metrics.Heatmap_Tracking.data.moves.push(moveData);

    // Keep only last 100 moves to prevent memory issues
    if (STATE.metrics.Heatmap_Tracking.data.moves.length > 100) {
      STATE.metrics.Heatmap_Tracking.data.moves.shift();
    }

    STATE.metrics.Heatmap_Tracking.lastUpdate = now;
    STATE.metrics.Heatmap_Tracking.count = STATE.metrics.Heatmap_Tracking.data.moves.length;
  }

  // Track Scroll
  let scrollThrottle = 0;
  function trackScroll() {
    const now = Date.now();
    if (now - scrollThrottle < 500) return; // Throttle to every 500ms
    scrollThrottle = now;

    const scrollData = {
      x: window.scrollX,
      y: window.scrollY,
      type: 'scroll',
      timestamp: now,
      depth: getScrollDepth(),
      page: window.location.pathname,
      sessionId: STATE.sessionId
    };

    STATE.metrics.Heatmap_Tracking.data.scrolls.push(scrollData);

    // Keep only last 50 scrolls
    if (STATE.metrics.Heatmap_Tracking.data.scrolls.length > 50) {
      STATE.metrics.Heatmap_Tracking.data.scrolls.shift();
    }
  }

  // Rage Click Detection - Detects rapid repeated clicks indicating user frustration
  function detectRageClick(event) {
    const now = Date.now();
    const target = event.target;
    const selector = getElementSelector(target);

    // Add current click to history
    STATE.clickHistory.push({
      timestamp: now,
      x: event.clientX,
      y: event.clientY,
      element: selector
    });

    // Keep only clicks from last 2 seconds
    STATE.clickHistory = STATE.clickHistory.filter(click => now - click.timestamp < 2000);

    // Rage click: 3+ clicks within 2 seconds in similar area (within 100px)
    if (STATE.clickHistory.length >= 3) {
      const recentClicks = STATE.clickHistory.slice(-3);
      const firstClick = recentClicks[0];
      const allNearby = recentClicks.every(click =>
        Math.abs(click.x - firstClick.x) < 100 &&
        Math.abs(click.y - firstClick.y) < 100
      );

      if (allNearby) {
        const rageClickData = {
          type: 'rage_click',
          timestamp: now,
          clickCount: STATE.clickHistory.length,
          duration: now - STATE.clickHistory[0].timestamp,
          x: event.clientX,
          y: event.clientY,
          pageX: event.pageX,
          pageY: event.pageY,
          element: selector,
          elementType: target.tagName.toLowerCase(),
          elementText: (target.textContent || '').substring(0, 50).trim(),
          page: window.location.pathname,
          url: window.location.href,
          sessionId: STATE.sessionId,
          visitorId: STATE.visitorId,
          customerDetails: getCustomerDetails()
        };

        STATE.metrics.Heatmap_Tracking.data.rageClicks.push(rageClickData);
        STATE.metrics.Heatmap_Tracking.lastUpdate = now;

        // Keep only last 20 rage clicks
        if (STATE.metrics.Heatmap_Tracking.data.rageClicks.length > 20) {
          STATE.metrics.Heatmap_Tracking.data.rageClicks.shift();
        }

        // Clear click history after detecting rage click
        STATE.clickHistory = [];

        if (CONFIG.debug) console.log('Rage click detected:', rageClickData);
      }
    }
  }

  // Dead Click Detection - Detects clicks that produce no response
  function detectDeadClick(event) {
    const target = event.target;
    const selector = getElementSelector(target);
    const tagName = target.tagName.toLowerCase();

    // Elements that should respond to clicks
    const interactiveElements = ['a', 'button', 'input', 'select', 'textarea', 'label'];
    const hasClickHandler = target.onclick || target.hasAttribute('onclick');
    const isInteractive = interactiveElements.includes(tagName) ||
                          target.hasAttribute('role') ||
                          target.hasAttribute('tabindex') ||
                          target.closest('a, button') ||
                          hasClickHandler;

    // Skip if element is clearly interactive
    if (isInteractive) return;

    // Check if element looks clickable (has cursor pointer style)
    const computedStyle = window.getComputedStyle(target);
    const looksClickable = computedStyle.cursor === 'pointer';

    // Dead click: Element looks clickable but isn't interactive
    if (looksClickable) {
      const deadClickData = {
        type: 'dead_click',
        timestamp: Date.now(),
        x: event.clientX,
        y: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
        element: selector,
        elementType: tagName,
        elementText: (target.textContent || '').substring(0, 50).trim(),
        computedCursor: computedStyle.cursor,
        page: window.location.pathname,
        url: window.location.href,
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        customerDetails: getCustomerDetails()
      };

      STATE.metrics.Heatmap_Tracking.data.deadClicks.push(deadClickData);
      STATE.metrics.Heatmap_Tracking.lastUpdate = Date.now();

      // Keep only last 20 dead clicks
      if (STATE.metrics.Heatmap_Tracking.data.deadClicks.length > 20) {
        STATE.metrics.Heatmap_Tracking.data.deadClicks.shift();
      }

      if (CONFIG.debug) console.log('Dead click detected:', deadClickData);
    }
  }

  // Helper: Get element selector
  function getElementSelector(element) {
    if (!element) return '';
    if (element.id) return '#' + element.id;
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
      return classes ? '.' + classes : element.tagName.toLowerCase();
    }
    return element.tagName.toLowerCase();
  }

  // Helper: Get element type
  function getElementType(element) {
    if (element.tagName === 'IMG') return 'image';
    if (element.tagName === 'BUTTON') return 'button';
    if (element.tagName === 'A') return 'link';
    if (/^H[1-6]$/.test(element.tagName)) return 'heading';
    return 'element';
  }

  // Helper: Get scroll depth
  function getScrollDepth() {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100 * 100) / 100 : 0;
  }

  // Helper: Get page type
  function getPageType() {
    const path = window.location.pathname;
    if (path.includes('/product/')) return 'product_detail';
    if (path.includes('/category/')) return 'category';
    if (path.includes('/search')) return 'search';
    if (path === '/') return 'homepage';
    return 'other';
  }

  // Track Heap Memory (if available)
  function trackHeapMemory() {
    if (performance.memory) {
      const usedMB = (performance.memory.usedJSHeapSize / 1048576);
      const totalMB = (performance.memory.totalJSHeapSize / 1048576);
      const limitMB = (performance.memory.jsHeapSizeLimit / 1048576);
      const usedPercentage = (performance.memory.usedJSHeapSize / performance.memory.totalJSHeapSize) * 100;

      const heap = {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        usedJSHeapSizeMB: usedMB.toFixed(2),
        totalJSHeapSizeMB: totalMB.toFixed(2),
        jsHeapSizeLimitMB: limitMB.toFixed(2),
        usedPercentage: usedPercentage.toFixed(2),
        availableHeapMB: (limitMB - usedMB).toFixed(2),
        formatted: {
          used: usedMB.toFixed(2) + ' MB',
          total: totalMB.toFixed(2) + ' MB',
          limit: limitMB.toFixed(2) + ' MB',
          percentage: usedPercentage.toFixed(2) + '%'
        }
      };

      STATE.metrics.Heap_Tracking.data = {
        type: 'heap_memory',
        timestamp: Date.now(),
        page: window.location.pathname,
        heap: heap,
        browser: navigator.userAgent,
        source: 'webtracking_sdk',
        heatmapData: STATE.metrics.Heatmap_Tracking.data,
        Click_Tracking: STATE.metrics.Product_Click_Tracking
      };
      STATE.metrics.Heap_Tracking.lastUpdate = Date.now();
      STATE.metrics.Heap_Tracking.count = 1;
    }
  }

  // Track Page View Event
  function trackPageView() {
    const eventData = {
      action: 'page_view',
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      source: 'webtracking_sdk',
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      metadata: {
        referrer: document.referrer || 'direct',
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        title: document.title,
        url: window.location.href
      }
    };

    if (!STATE.metrics.Event_Tracking.data.events) {
      STATE.metrics.Event_Tracking.data.events = [];
    }
    STATE.metrics.Event_Tracking.data.events.push(eventData);
    STATE.metrics.Event_Tracking.lastUpdate = Date.now();
    STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;
  }

  // Track Pixel/Conversion Events
  function trackPixelEvent(type, productId = null) {
    if (!STATE.metrics.Pixel_Tracking.data.conversions) {
      STATE.metrics.Pixel_Tracking.data.conversions = [];
    }

    const conversion = {
      type: type,
      value: 1,
      timestamp: Date.now(),
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    if (productId) {
      conversion.productId = productId;
    }

    STATE.metrics.Pixel_Tracking.data.conversions.push(conversion);
    STATE.metrics.Pixel_Tracking.data.type = 'tracking_pixel';
    STATE.metrics.Pixel_Tracking.data.timestamp = Date.now();
    STATE.metrics.Pixel_Tracking.data.page = window.location.pathname;
    STATE.metrics.Pixel_Tracking.data.source = 'webtracking_sdk';
    STATE.metrics.Pixel_Tracking.lastUpdate = Date.now();
    STATE.metrics.Pixel_Tracking.count = STATE.metrics.Pixel_Tracking.data.conversions.length;
  }

  // Track Cookie Operations
  function trackCookieOperation(action, name, value = null, expiryDays = null) {
    if (!STATE.metrics.Cookie_Operations.data.operations) {
      STATE.metrics.Cookie_Operations.data.operations = [];
    }

    const operation = {
      action: action,
      name: name,
      timestamp: Date.now(),
      page: window.location.pathname
    };

    if (value !== null) operation.value = value;
    if (expiryDays !== null) operation.expiryDays = expiryDays;

    STATE.metrics.Cookie_Operations.data.operations.push(operation);

    // Update active cookies list
    if (!STATE.metrics.Cookie_Operations.data.activeCookies) {
      STATE.metrics.Cookie_Operations.data.activeCookies = {};
    }

    if (action === 'set') {
      STATE.metrics.Cookie_Operations.data.activeCookies[name] = value;
    } else if (action === 'delete') {
      delete STATE.metrics.Cookie_Operations.data.activeCookies[name];
    }

    STATE.metrics.Cookie_Operations.lastUpdate = Date.now();
    STATE.metrics.Cookie_Operations.count = STATE.metrics.Cookie_Operations.data.operations.length;
  }

  // =========================================================================
  // SHOPIFY CART TRACKING
  // =========================================================================

  // Initialize Shopify Cart Tracking
  function initShopifyCartTracking() {
    // Get initial cart state
    fetchShopifyCart();

    // Intercept Shopify AJAX cart requests
    interceptCartRequests();

    // Listen for Shopify cart events
    listenForCartEvents();

    // Watch for add-to-cart button clicks
    watchAddToCartButtons();

    if (CONFIG.debug) console.log('Shopify cart tracking initialized');
  }

  // Fetch current Shopify cart
  async function fetchShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        updateCartState(cart);
        return cart;
      }
    } catch (error) {
      if (CONFIG.debug) console.warn('Failed to fetch cart:', error);
    }
    return null;
  }

  // Update internal cart state
  function updateCartState(cart) {
    if (!cart) return;

    STATE.cart = {
      token: cart.token,
      items: cart.items.map(item => ({
        id: item.id,
        variantId: item.variant_id,
        productId: item.product_id,
        title: item.title,
        productTitle: item.product_title,
        variantTitle: item.variant_title,
        sku: item.sku,
        vendor: item.vendor,
        quantity: item.quantity,
        price: item.price / 100,
        linePrice: item.line_price / 100,
        originalPrice: item.original_price / 100,
        discountedPrice: item.discounted_price / 100,
        image: item.image,
        url: item.url,
        handle: item.handle,
        properties: item.properties
      })),
      itemCount: cart.item_count,
      totalPrice: cart.total_price / 100,
      totalDiscount: cart.total_discount / 100,
      currency: cart.currency,
      requiresShipping: cart.requires_shipping
    };

    // Update metrics
    STATE.metrics.Cart_Tracking.data.currentCart = STATE.cart;
    STATE.metrics.Cart_Tracking.lastUpdate = Date.now();
  }

  // Track cart event
  function trackCartEvent(action, data) {
    const cartEvent = {
      action: action,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      cartData: data,
      cartSummary: {
        itemCount: STATE.cart.itemCount,
        totalPrice: STATE.cart.totalPrice,
        currency: STATE.cart.currency
      }
    };

    STATE.metrics.Cart_Tracking.data.events.push(cartEvent);
    STATE.metrics.Cart_Tracking.lastUpdate = Date.now();
    STATE.metrics.Cart_Tracking.count = STATE.metrics.Cart_Tracking.data.events.length;

    // Also track as general event
    trackGeneralEvent('cart_' + action, data);

    // Track as pixel event for conversions
    if (action === 'add' || action === 'checkout') {
      trackPixelEvent('cart_' + action, data.productId || null);
    }

    if (CONFIG.debug) console.log('Cart event tracked:', cartEvent);
  }

  // Intercept cart AJAX requests
  function interceptCartRequests() {
    // Store original fetch
    STATE.originalFetch = window.fetch;

    // Override fetch to intercept cart requests
    window.fetch = async function(...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url.url;

      // Call original fetch
      const response = await STATE.originalFetch.apply(this, args);

      // Check if it's a Shopify cart request
      if (urlString.includes('/cart/')) {
        try {
          // Clone response to read body
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();

          // Detect cart action type
          if (urlString.includes('/cart/add')) {
            handleCartAdd(data, options);
          } else if (urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
            handleCartUpdate(data, options);
          } else if (urlString.includes('/cart/clear')) {
            handleCartClear(data);
          }

          // Update cart state
          if (data.items !== undefined) {
            updateCartState(data);
          }
        } catch (e) {
          // Ignore JSON parse errors for non-JSON responses
        }
      }

      return response;
    };

    // Also intercept XMLHttpRequest for older Shopify themes
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    STATE.originalXHROpen = originalXHROpen;
    STATE.originalXHRSend = originalXHRSend;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._trackingUrl = url;
      this._trackingMethod = method;
      return originalXHROpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(body) {
      const xhr = this;
      const url = this._trackingUrl;

      if (url && url.includes('/cart/')) {
        xhr.addEventListener('load', function() {
          try {
            const data = JSON.parse(xhr.responseText);

            if (url.includes('/cart/add')) {
              handleCartAdd(data, { body: body });
            } else if (url.includes('/cart/change') || url.includes('/cart/update')) {
              handleCartUpdate(data, { body: body });
            } else if (url.includes('/cart/clear')) {
              handleCartClear(data);
            }

            if (data.items !== undefined) {
              updateCartState(data);
            }
          } catch (e) {
            // Ignore
          }
        });
      }

      return originalXHRSend.apply(this, arguments);
    };
  }

  // Handle add to cart
  function handleCartAdd(response, options) {
    let addedItem = null;

    // Parse request body to get added item details
    if (options && options.body) {
      try {
        const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        addedItem = {
          variantId: body.id || body.variant_id,
          quantity: body.quantity || 1,
          properties: body.properties
        };
      } catch (e) {
        // Try form data parsing
        if (typeof options.body === 'string') {
          const params = new URLSearchParams(options.body);
          addedItem = {
            variantId: params.get('id') || params.get('variant_id'),
            quantity: parseInt(params.get('quantity')) || 1
          };
        }
      }
    }

    // Get item details from response
    const itemData = response.items ? response.items[response.items.length - 1] : response;

    trackCartEvent('add', {
      productId: itemData.product_id,
      variantId: itemData.variant_id || itemData.id,
      title: itemData.title || itemData.product_title,
      variantTitle: itemData.variant_title,
      sku: itemData.sku,
      price: (itemData.price || 0) / 100,
      quantity: addedItem?.quantity || itemData.quantity || 1,
      image: itemData.image,
      vendor: itemData.vendor,
      handle: itemData.handle
    });
  }

  // Handle cart update
  function handleCartUpdate(response, options) {
    trackCartEvent('update', {
      itemCount: response.item_count,
      totalPrice: response.total_price / 100,
      items: response.items?.map(item => ({
        productId: item.product_id,
        variantId: item.variant_id,
        title: item.title,
        quantity: item.quantity,
        price: item.price / 100
      }))
    });
  }

  // Handle cart clear
  function handleCartClear(response) {
    trackCartEvent('clear', {
      previousItemCount: STATE.cart.itemCount,
      previousTotalPrice: STATE.cart.totalPrice
    });
  }

  // Listen for Shopify-specific cart events
  function listenForCartEvents() {
    // Listen for custom Shopify events
    document.addEventListener('cart:updated', function(e) {
      fetchShopifyCart();
    });

    // Listen for theme-specific events
    document.addEventListener('ajaxCart.afterCartLoad', function(e) {
      fetchShopifyCart();
    });

    // Shopify section rendering events
    document.addEventListener('shopify:section:load', function(e) {
      if (e.detail && e.detail.sectionId && e.detail.sectionId.includes('cart')) {
        fetchShopifyCart();
      }
    });
  }

  // Watch for add-to-cart button clicks
  function watchAddToCartButtons() {
    document.addEventListener('click', function(e) {
      const target = e.target;

      // Check if clicked element is an add-to-cart button
      const addToCartButton = target.closest('[data-add-to-cart], [name="add"], .add-to-cart, .product-form__submit, [type="submit"][name="add"]');

      if (addToCartButton) {
        // Find the product form
        const form = addToCartButton.closest('form[action*="/cart/add"]');
        if (form) {
          const productData = extractProductDataFromForm(form);
          if (productData) {
            trackCartEvent('add_click', productData);
          }
        }
      }
    });

    // Watch for form submissions
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        const productData = extractProductDataFromForm(form);
        if (productData) {
          trackCartEvent('add_submit', productData);
        }
      }
    });
  }

  // Extract product data from form
  function extractProductDataFromForm(form) {
    const variantSelect = form.querySelector('[name="id"], select[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const productElement = form.closest('[data-product-id]') || document.querySelector('[data-product-id]');

    return {
      variantId: variantSelect?.value,
      quantity: parseInt(quantityInput?.value) || 1,
      productId: productElement?.getAttribute('data-product-id') || form.getAttribute('data-product-id'),
      productTitle: productElement?.getAttribute('data-product-title') || document.querySelector('.product__title, .product-single__title, h1.title')?.textContent?.trim(),
      productPrice: productElement?.getAttribute('data-product-price') || document.querySelector('.product__price, .price, [data-product-price]')?.textContent?.replace(/[^0-9.]/g, '')
    };
  }

  // Track checkout initiation
  function trackCheckout(checkoutData = {}) {
    trackCartEvent('checkout_initiated', {
      ...STATE.cart,
      ...checkoutData,
      checkoutUrl: window.location.href
    });
  }

  // =========================================================================
  // PAGE PERFORMANCE TRACKING
  // =========================================================================

  // Initialize page performance tracking
  function initPagePerformanceTracking() {
    // Wait for page load to complete
    if (document.readyState === 'complete') {
      trackPagePerformance();
    } else {
      window.addEventListener('load', function() {
        // Delay slightly to ensure all metrics are available
        setTimeout(trackPagePerformance, 100);
      });
    }

    // Track Web Vitals if available
    trackWebVitals();

    // Track resource loading errors
    trackResourceErrors();
  }

  // Track page performance metrics
  function trackPagePerformance() {
    const performance = window.performance;
    if (!performance) return;

    const timing = performance.timing || {};
    const navigation = performance.getEntriesByType('navigation')[0] || {};

    const perfData = {
      // Navigation Timing
      pageLoadTime: timing.loadEventEnd - timing.navigationStart,
      domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
      domInteractive: timing.domInteractive - timing.navigationStart,
      domComplete: timing.domComplete - timing.navigationStart,

      // Network Timing
      dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
      tcpConnection: timing.connectEnd - timing.connectStart,
      serverResponse: timing.responseEnd - timing.requestStart,
      contentDownload: timing.responseEnd - timing.responseStart,

      // Render Timing
      firstByte: timing.responseStart - timing.navigationStart,
      firstPaint: 0,
      firstContentfulPaint: 0,
      largestContentfulPaint: 0,

      // Page Info
      page: window.location.pathname,
      url: window.location.href,
      timestamp: Date.now(),
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,

      // Resource counts
      resourceCount: performance.getEntriesByType('resource').length,
      transferSize: 0,

      // Connection info
      connectionType: navigator.connection?.effectiveType || 'unknown',
      downlink: navigator.connection?.downlink || 0,
      rtt: navigator.connection?.rtt || 0
    };

    // Get paint timing
    const paintEntries = performance.getEntriesByType('paint');
    paintEntries.forEach(entry => {
      if (entry.name === 'first-paint') {
        perfData.firstPaint = Math.round(entry.startTime);
      } else if (entry.name === 'first-contentful-paint') {
        perfData.firstContentfulPaint = Math.round(entry.startTime);
      }
    });

    // Calculate total transfer size
    const resources = performance.getEntriesByType('resource');
    perfData.transferSize = resources.reduce((total, resource) => total + (resource.transferSize || 0), 0);
    perfData.transferSizeMB = (perfData.transferSize / 1048576).toFixed(2);

    // Categorize performance
    perfData.performanceGrade = getPerformanceGrade(perfData);

    STATE.metrics.Page_Performance.data = perfData;
    STATE.metrics.Page_Performance.lastUpdate = Date.now();
    STATE.metrics.Page_Performance.count = 1;

    if (CONFIG.debug) console.log('Page performance tracked:', perfData);
  }

  // Get performance grade
  function getPerformanceGrade(perfData) {
    const loadTime = perfData.pageLoadTime;
    if (loadTime < 1000) return 'excellent';
    if (loadTime < 2500) return 'good';
    if (loadTime < 4000) return 'needs_improvement';
    return 'poor';
  }

  // Track Web Vitals (LCP, FID, CLS)
  function trackWebVitals() {
    // Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          STATE.metrics.Page_Performance.data.largestContentfulPaint = Math.round(lastEntry.startTime);
          STATE.metrics.Page_Performance.lastUpdate = Date.now();
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // First Input Delay
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach(entry => {
            STATE.metrics.Page_Performance.data.firstInputDelay = Math.round(entry.processingStart - entry.startTime);
            STATE.metrics.Page_Performance.lastUpdate = Date.now();
          });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });

        // Cumulative Layout Shift
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach(entry => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          STATE.metrics.Page_Performance.data.cumulativeLayoutShift = clsValue.toFixed(4);
          STATE.metrics.Page_Performance.lastUpdate = Date.now();
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });

      } catch (e) {
        if (CONFIG.debug) console.warn('Web Vitals tracking error:', e);
      }
    }
  }

  // Track resource loading errors
  function trackResourceErrors() {
    window.addEventListener('error', function(e) {
      // Check if it's a resource loading error
      if (e.target && (e.target.tagName === 'IMG' || e.target.tagName === 'SCRIPT' || e.target.tagName === 'LINK')) {
        trackError({
          type: 'resource_load_error',
          resourceType: e.target.tagName.toLowerCase(),
          resourceUrl: e.target.src || e.target.href,
          message: 'Failed to load resource',
          page: window.location.pathname
        });
      }
    }, true);
  }

  // =========================================================================
  // ERROR TRACKING
  // =========================================================================

  // Initialize error tracking
  function initErrorTracking() {
    // Global error handler
    window.addEventListener('error', function(e) {
      trackError({
        type: 'javascript_error',
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack?.substring(0, 1000)
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', function(e) {
      trackError({
        type: 'unhandled_promise_rejection',
        message: e.reason?.message || String(e.reason),
        stack: e.reason?.stack?.substring(0, 1000)
      });
    });

    // Console error interception
    const originalConsoleError = console.error;
    console.error = function(...args) {
      trackError({
        type: 'console_error',
        message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ').substring(0, 500)
      });
      originalConsoleError.apply(console, args);
    };

    if (CONFIG.debug) console.log('Error tracking initialized');
  }

  // Track error
  function trackError(errorData) {
    const error = {
      ...errorData,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    STATE.metrics.Error_Tracking.data.errors.push(error);
    STATE.metrics.Error_Tracking.lastUpdate = Date.now();
    STATE.metrics.Error_Tracking.count = STATE.metrics.Error_Tracking.data.errors.length;

    // Keep only last 50 errors
    if (STATE.metrics.Error_Tracking.data.errors.length > 50) {
      STATE.metrics.Error_Tracking.data.errors.shift();
    }

    // Also track as general event
    trackGeneralEvent('error_occurred', { type: errorData.type, message: errorData.message?.substring(0, 100) });

    if (CONFIG.debug) console.log('Error tracked:', error);
  }

  // =========================================================================
  // NETWORK REQUEST TRACKING
  // =========================================================================

  // Initialize network tracking
  function initNetworkTracking() {
    // Track fetch requests
    interceptFetchForTracking();

    // Track XHR requests
    interceptXHRForTracking();

    // Track failed image/script loads
    trackFailedResources();

    if (CONFIG.debug) console.log('Network tracking initialized');
  }

  // Intercept fetch for network tracking
  function interceptFetchForTracking() {
    const originalFetch = STATE.originalFetch || window.fetch;

    window.fetch = async function(...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url.url;
      const method = options?.method || 'GET';
      const startTime = Date.now();

      try {
        const response = await originalFetch.apply(this, args);
        const duration = Date.now() - startTime;

        // Track successful request
        trackNetworkRequest({
          url: urlString,
          method: method,
          status: response.status,
          statusText: response.statusText,
          duration: duration,
          success: response.ok
        });

        // Track failed requests separately
        if (!response.ok) {
          trackFailedRequest({
            url: urlString,
            method: method,
            status: response.status,
            statusText: response.statusText,
            duration: duration
          });
        }

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;

        // Track network error
        trackFailedRequest({
          url: urlString,
          method: method,
          status: 0,
          statusText: 'Network Error',
          duration: duration,
          error: error.message
        });

        throw error;
      }
    };
  }

  // Intercept XHR for network tracking
  function interceptXHRForTracking() {
    const originalOpen = STATE.originalXHROpen || XMLHttpRequest.prototype.open;
    const originalSend = STATE.originalXHRSend || XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._networkTrackingUrl = url;
      this._networkTrackingMethod = method;
      return originalOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(body) {
      const xhr = this;
      const startTime = Date.now();

      xhr.addEventListener('loadend', function() {
        const duration = Date.now() - startTime;

        trackNetworkRequest({
          url: xhr._networkTrackingUrl,
          method: xhr._networkTrackingMethod,
          status: xhr.status,
          statusText: xhr.statusText,
          duration: duration,
          success: xhr.status >= 200 && xhr.status < 400,
          type: 'xhr'
        });

        if (xhr.status >= 400 || xhr.status === 0) {
          trackFailedRequest({
            url: xhr._networkTrackingUrl,
            method: xhr._networkTrackingMethod,
            status: xhr.status,
            statusText: xhr.statusText,
            duration: duration,
            type: 'xhr'
          });
        }
      });

      return originalSend.apply(this, arguments);
    };
  }

  // Track network request
  function trackNetworkRequest(requestData) {
    // Only track API/data requests, not static assets
    const url = requestData.url || '';
    if (url.match(/\.(js|css|png|jpg|jpeg|gif|svg|woff|woff2|ttf|ico)(\?|$)/i)) {
      return; // Skip static assets
    }

    const request = {
      ...requestData,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId
    };

    STATE.metrics.Network_Tracking.data.requests.push(request);
    STATE.metrics.Network_Tracking.lastUpdate = Date.now();
    STATE.metrics.Network_Tracking.count = STATE.metrics.Network_Tracking.data.requests.length;

    // Keep only last 100 requests
    if (STATE.metrics.Network_Tracking.data.requests.length > 100) {
      STATE.metrics.Network_Tracking.data.requests.shift();
    }
  }

  // Track failed request
  function trackFailedRequest(requestData) {
    const failedRequest = {
      ...requestData,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId
    };

    STATE.metrics.Network_Tracking.data.failedRequests.push(failedRequest);

    // Keep only last 30 failed requests
    if (STATE.metrics.Network_Tracking.data.failedRequests.length > 30) {
      STATE.metrics.Network_Tracking.data.failedRequests.shift();
    }

    if (CONFIG.debug) console.log('Failed request tracked:', failedRequest);
  }

  // Track failed resource loads
  function trackFailedResources() {
    // Already handled in trackResourceErrors()
  }

  // =========================================================================
  // USER AUTHENTICATION TRACKING
  // =========================================================================

  // Initialize user auth tracking
  function initUserAuthTracking() {
    // Watch for login form submissions
    watchAuthForms();

    // Check for logged-in state changes
    detectAuthStateChanges();

    // Track current user state
    trackCurrentUserState();

    // Watch for social/OAuth login buttons
    watchSocialLoginButtons();

    // Monitor for Shopify Multipass and token-based logins
    detectTokenBasedLogin();

    // Watch for checkout-based sign-in (guest -> account during checkout)
    watchCheckoutSignIn();

    if (CONFIG.debug) console.log('User auth tracking initialized');
  }

  // Watch for login/register form submissions with full detail capture
  function watchAuthForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;
      const action = form.action || '';

      if (action.includes('/account/login') || form.id === 'customer_login') {
        // Capture the email being used for login
        const emailInput = form.querySelector('input[type="email"], input[name="customer[email]"]');
        const signInEmail = emailInput ? emailInput.value : null;

        trackAuthEvent('login_attempt', {
          formId: form.id,
          hasEmail: !!signInEmail,
          loginMethod: 'email_password'
        });

        // Capture sign-in identity for binding after success
        if (signInEmail) {
          captureSignInIdentity({
            email: signInEmail,
            method: 'email_password',
            source: 'login_form',
            formId: form.id
          });
        }
      }

      if (action.includes('/account/register') || action.includes('/account') && form.querySelector('input[name="customer[password_confirmation]"]')) {
        // Capture registration details
        const regEmail = form.querySelector('input[type="email"], input[name="customer[email]"]');
        const regFirstName = form.querySelector('input[name="customer[first_name]"]');
        const regLastName = form.querySelector('input[name="customer[last_name]"]');
        const regPhone = form.querySelector('input[type="tel"], input[name="customer[phone]"]');

        var registrationData = {
          formId: form.id,
          loginMethod: 'registration'
        };

        trackAuthEvent('register_attempt', registrationData);

        // Capture registration identity
        captureSignInIdentity({
          email: regEmail ? regEmail.value : null,
          firstName: regFirstName ? regFirstName.value : null,
          lastName: regLastName ? regLastName.value : null,
          phone: regPhone ? regPhone.value : null,
          method: 'registration',
          source: 'register_form',
          formId: form.id
        });
      }

      if (action.includes('/account/recover') || form.id === 'recover_customer_password') {
        var recoveryEmail = form.querySelector('input[type="email"], input[name="email"]');
        trackAuthEvent('password_recovery_attempt', {
          formId: form.id,
          hasEmail: !!(recoveryEmail && recoveryEmail.value)
        });
      }
    });
  }

  // Watch for social/OAuth login buttons (Google, Facebook, Apple, Shopify)
  function watchSocialLoginButtons() {
    document.addEventListener('click', function(e) {
      const target = e.target;

      // Common social login button selectors across Shopify themes and apps
      const socialButton = target.closest(
        '[data-login-with="google"], [data-login-with="facebook"], [data-login-with="apple"], ' +
        '[data-provider="google"], [data-provider="facebook"], [data-provider="apple"], ' +
        '.social-login-btn, .social-login__btn, .shopify-login-btn, ' +
        'a[href*="auth/google"], a[href*="auth/facebook"], a[href*="auth/apple"], ' +
        'a[href*="/account/social/"], button[data-social-login], ' +
        '.auth0-lock-social-button, [data-action="social-login"]'
      );

      if (socialButton) {
        var provider = socialButton.getAttribute('data-login-with') ||
                       socialButton.getAttribute('data-provider') ||
                       detectSocialProviderFromElement(socialButton);

        trackAuthEvent('social_login_attempt', {
          provider: provider,
          loginMethod: 'social_' + (provider || 'unknown')
        });

        captureSignInIdentity({
          method: 'social_' + (provider || 'unknown'),
          source: 'social_login_button',
          provider: provider
        });
      }
    });
  }

  // Detect social provider from element content/attributes
  function detectSocialProviderFromElement(el) {
    var text = (el.textContent || '').toLowerCase();
    var href = (el.href || el.getAttribute('data-href') || '').toLowerCase();
    var className = (el.className || '').toLowerCase();
    var combined = text + ' ' + href + ' ' + className;

    if (combined.includes('google')) return 'google';
    if (combined.includes('facebook') || combined.includes('fb')) return 'facebook';
    if (combined.includes('apple')) return 'apple';
    if (combined.includes('twitter') || combined.includes('x.com')) return 'twitter';
    if (combined.includes('shopify')) return 'shopify';
    return null;
  }

  // Detect token-based logins (Shopify Multipass, magic links)
  function detectTokenBasedLogin() {
    var params = new URLSearchParams(window.location.search);

    // Shopify Multipass token
    if (params.has('multipass') || params.has('token')) {
      trackAuthEvent('multipass_login_detected', {
        loginMethod: 'multipass',
        hasToken: true
      });

      captureSignInIdentity({
        method: 'multipass',
        source: 'url_token'
      });
    }

    // Magic link / email login link
    if (params.has('login_token') || params.has('magic') || window.location.pathname.includes('/account/activate')) {
      trackAuthEvent('magic_link_login_detected', {
        loginMethod: 'magic_link'
      });

      captureSignInIdentity({
        method: 'magic_link',
        source: 'url_token'
      });
    }
  }

  // Watch for checkout-based sign-in (when customer logs in during checkout)
  function watchCheckoutSignIn() {
    var path = window.location.pathname;
    if (!path.includes('/checkouts/')) return;

    // Watch for "Log in" link clicks within checkout
    document.addEventListener('click', function(e) {
      var target = e.target;
      var loginLink = target.closest('a[href*="/account/login"], [data-login-link], .step__footer__previous-link');
      if (loginLink && loginLink.textContent && loginLink.textContent.toLowerCase().includes('log in')) {
        trackAuthEvent('checkout_login_attempt', {
          loginMethod: 'checkout_login',
          checkoutUrl: window.location.href
        });
      }
    });

    // Watch for "Remember me" checkbox on checkout
    var rememberMeCheckbox = document.querySelector('#checkout_remember_me, input[name="checkout[remember_me]"]');
    if (rememberMeCheckbox) {
      rememberMeCheckbox.addEventListener('change', function() {
        trackAuthEvent('checkout_remember_me_toggled', {
          checked: rememberMeCheckbox.checked
        });
      });
    }
  }

  // Detect authentication state changes with comprehensive sign-in capture
  function detectAuthStateChanges() {
    var path = window.location.pathname;
    var referrer = document.referrer;

    if (path.includes('/account/login')) {
      trackAuthEvent('login_page_view', {});
    } else if (path.includes('/account/register')) {
      trackAuthEvent('register_page_view', {});
    } else if (path.includes('/account') && !path.includes('/login') && !path.includes('/register') && !path.includes('/recover') && !path.includes('/activate')) {
      trackAuthEvent('account_page_view', { isLoggedIn: true });
    }

    // Detect successful login — check multiple referrer sources
    var isFromLogin = referrer.includes('/account/login');
    var isFromRegister = referrer.includes('/account/register');
    var isFromActivate = referrer.includes('/account/activate');
    var isFromSocial = referrer.includes('/auth/') || referrer.includes('/social/');
    var isOnAccount = path.includes('/account') && !path.includes('/login') && !path.includes('/register');

    if (isOnAccount && (isFromLogin || isFromRegister || isFromActivate || isFromSocial)) {
      var loginMethod = 'unknown';
      if (isFromLogin) loginMethod = 'email_password';
      if (isFromRegister) loginMethod = 'registration';
      if (isFromActivate) loginMethod = 'account_activation';
      if (isFromSocial) loginMethod = 'social';

      // Get the full customer details since we're now logged in
      var customer = getShopifyCustomer();
      var signInData = {
        loginMethod: loginMethod,
        referrer: referrer,
        isNewSession: !sessionStorage.getItem('wt_signin_tracked')
      };

      if (signInData.isNewSession) {
        trackAuthEvent('login_success', signInData);
        captureSignInIdentity({
          email: customer ? customer.email : null,
          firstName: customer ? customer.firstName : null,
          lastName: customer ? customer.lastName : null,
          phone: customer ? customer.mobile : null,
          customerId: customer ? customer.customerId : null,
          method: loginMethod,
          source: 'redirect_detection'
        });
        sessionStorage.setItem('wt_signin_tracked', 'true');
      }
    }

    // Detect logout
    if (path.includes('/account/login') && referrer.includes('/account') && !referrer.includes('/login')) {
      trackAuthEvent('logout_detected', {
        referrer: referrer
      });
      sessionStorage.removeItem('wt_signin_tracked');
    }
  }

  // Track current user state — capture full identity on page load if signed in
  function trackCurrentUserState() {
    var customer = getShopifyCustomer();

    if (customer && (customer.customerId || customer.email)) {
      // User is currently signed in — build comprehensive state
      var signInState = {
        isLoggedIn: true,
        customerId: customer.customerId || null,
        email: customer.email || null,
        firstName: customer.firstName || null,
        lastName: customer.lastName || null,
        name: customer.name || null,
        phone: customer.mobile || null,
        ordersCount: customer.ordersCount || null,
        totalSpent: customer.totalSpent || null,
        tags: customer.tags || null,
        acceptsMarketing: customer.acceptsMarketing || null,
        detectedAt: Date.now(),
        detectionMethod: 'page_load',
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId
      };

      // Store current sign-in state
      STATE.metrics.User_Auth_Tracking.data.currentSignInState = signInState;

      trackAuthEvent('user_identified', {
        isLoggedIn: true,
        hasEmail: !!customer.email,
        hasCustomerId: !!customer.customerId,
        loginMethod: 'existing_session'
      });

      // Capture and persist sign-in identity binding
      captureSignInIdentity({
        email: customer.email,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.mobile,
        customerId: customer.customerId,
        ordersCount: customer.ordersCount,
        totalSpent: customer.totalSpent,
        tags: customer.tags,
        acceptsMarketing: customer.acceptsMarketing,
        method: 'existing_session',
        source: 'page_load_detection'
      });
    } else {
      STATE.metrics.User_Auth_Tracking.data.currentSignInState = {
        isLoggedIn: false,
        detectedAt: Date.now(),
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId
      };
    }
  }

  // Capture and record sign-in identity — the core function that binds
  // visitor/session IDs to customer identity for cross-session tracking
  function captureSignInIdentity(identityData) {
    var now = Date.now();

    var signInCapture = {
      timestamp: now,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      page: window.location.pathname,
      url: window.location.href,
      identity: {
        email: identityData.email || null,
        firstName: identityData.firstName || null,
        lastName: identityData.lastName || null,
        name: identityData.firstName && identityData.lastName
          ? identityData.firstName + ' ' + identityData.lastName
          : identityData.name || null,
        phone: identityData.phone || null,
        customerId: identityData.customerId || null,
        ordersCount: identityData.ordersCount || null,
        totalSpent: identityData.totalSpent || null,
        tags: identityData.tags || null,
        acceptsMarketing: identityData.acceptsMarketing || null
      },
      signInMethod: identityData.method || 'unknown',
      signInSource: identityData.source || 'unknown',
      provider: identityData.provider || null,
      formId: identityData.formId || null,
      webMetrics: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: screen.width + 'x' + screen.height,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        referrer: document.referrer,
        sessionDuration: now - STATE.pageLoadTime
      }
    };

    // Store in sign-in captures
    STATE.metrics.User_Auth_Tracking.data.signInCaptures.push(signInCapture);
    STATE.metrics.User_Auth_Tracking.lastUpdate = now;

    // Update customer details with captured identity
    if (identityData.email || identityData.customerId) {
      updateCustomerDetails({
        email: identityData.email,
        mobile: identityData.phone,
        firstName: identityData.firstName,
        lastName: identityData.lastName,
        customerId: identityData.customerId,
        ordersCount: identityData.ordersCount,
        totalSpent: identityData.totalSpent,
        tags: identityData.tags,
        acceptsMarketing: identityData.acceptsMarketing
      });
    }

    // Emit sign-in identity to session recording for cross-reference
    if (window.SessionRecording && typeof window.SessionRecording.addCustomEvent === 'function') {
      window.SessionRecording.addCustomEvent('customer_sign_in', {
        email: identityData.email || null,
        customerId: identityData.customerId || null,
        name: signInCapture.identity.name,
        method: identityData.method,
        timestamp: now
      });
    }

    // Track as general event for cross-module visibility
    trackGeneralEvent('customer_sign_in_captured', {
      hasEmail: !!identityData.email,
      hasCustomerId: !!identityData.customerId,
      method: identityData.method,
      source: identityData.source
    });

    // Track as pixel event for conversion attribution
    trackPixelEvent('customer_sign_in');

    // Immediately flush metrics to bind identity to session
    sendMetrics();

    if (CONFIG.debug) console.log('Sign-in identity captured:', signInCapture);
  }

  // Track auth event
  function trackAuthEvent(action, data) {
    const authEvent = {
      action: action,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      data: data
    };

    STATE.metrics.User_Auth_Tracking.data.events.push(authEvent);
    STATE.metrics.User_Auth_Tracking.lastUpdate = Date.now();
    STATE.metrics.User_Auth_Tracking.count = STATE.metrics.User_Auth_Tracking.data.events.length;

    // Keep only last 50 auth events (increased from 20 for comprehensive tracking)
    if (STATE.metrics.User_Auth_Tracking.data.events.length > 50) {
      STATE.metrics.User_Auth_Tracking.data.events.shift();
    }

    // Also track as general event
    trackGeneralEvent('auth_' + action, data);

    if (CONFIG.debug) console.log('Auth event tracked:', authEvent);
  }

  // =========================================================================
  // ORDER/PURCHASE TRACKING (E-commerce)
  // =========================================================================

  // Initialize Order Tracking
  function initOrderTracking() {
    // Detect Shopify checkout/thank you pages
    detectCheckoutPages();

    // Listen for Shopify checkout events
    listenForCheckoutEvents();

    // Track order from thank you page
    trackOrderFromThankYouPage();

    if (CONFIG.debug) console.log('Order tracking initialized');
  }

  // Detect checkout pages
  function detectCheckoutPages() {
    const path = window.location.pathname;
    const hostname = window.location.hostname;

    // Shopify checkout detection
    if (hostname.includes('checkout.shopify.com') || path.includes('/checkouts/')) {
      trackCheckoutStep('checkout_started', {
        checkoutUrl: window.location.href
      });
    }

    // Contact information step
    if (path.includes('/checkouts/') && (path.includes('/contact_information') || document.querySelector('[data-step="contact_information"]'))) {
      trackCheckoutStep('contact_information', {});
    }

    // Shipping step
    if (path.includes('/checkouts/') && (path.includes('/shipping') || document.querySelector('[data-step="shipping_method"]'))) {
      trackCheckoutStep('shipping_method', {});
    }

    // Payment step
    if (path.includes('/checkouts/') && (path.includes('/payment') || document.querySelector('[data-step="payment_method"]'))) {
      trackCheckoutStep('payment_method', {});
    }

    // Order confirmation / Thank you page
    if (path.includes('/thank_you') || path.includes('/orders/') || path.includes('/checkout/thank_you')) {
      trackCheckoutStep('order_complete', {});
    }
  }

  // Listen for Shopify checkout events
  function listenForCheckoutEvents() {
    // Shopify Analytics events
    if (window.Shopify && window.Shopify.Checkout) {
      const checkout = window.Shopify.Checkout;

      if (checkout.step) {
        trackCheckoutStep(checkout.step, {
          token: checkout.token,
          page: checkout.page
        });
      }
    }

    // Listen for Shopify's analytics events
    document.addEventListener('page:load', function() {
      detectCheckoutPages();
    });

    // Shopify Plus checkout events
    if (window.Shopify && window.Shopify.checkout) {
      trackCheckoutStep('checkout_loaded', {
        token: window.Shopify.checkout.token,
        subtotalPrice: window.Shopify.checkout.subtotal_price,
        totalPrice: window.Shopify.checkout.total_price
      });
    }
  }

  // Track checkout step
  function trackCheckoutStep(step, data) {
    const stepData = {
      step: step,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      cart: STATE.cart,
      ...data
    };

    STATE.metrics.Order_Tracking.data.checkoutSteps.push(stepData);
    STATE.metrics.Order_Tracking.lastUpdate = Date.now();

    // Track as general event
    trackGeneralEvent('checkout_' + step, data);

    // Track as pixel event
    trackPixelEvent('checkout_' + step);

    if (CONFIG.debug) console.log('Checkout step tracked:', stepData);
  }

  // Track order from thank you page
  function trackOrderFromThankYouPage() {
    // Check if we're on thank you page
    const path = window.location.pathname;
    if (!path.includes('/thank_you') && !path.includes('/orders/')) return;

    // Try to get order data from Shopify
    let orderData = null;

    // Method 1: Shopify.checkout object
    if (window.Shopify && window.Shopify.checkout) {
      const checkout = window.Shopify.checkout;
      orderData = {
        orderId: checkout.order_id,
        orderNumber: checkout.order_number || checkout.order_id,
        token: checkout.token,
        email: checkout.email,
        currency: checkout.currency,
        subtotalPrice: parseFloat(checkout.subtotal_price) / 100,
        totalTax: parseFloat(checkout.total_tax) / 100,
        totalPrice: parseFloat(checkout.total_price) / 100,
        totalShipping: parseFloat(checkout.shipping_price || 0) / 100,
        totalDiscount: parseFloat(checkout.total_discounts || 0) / 100,
        lineItems: checkout.line_items?.map(item => ({
          productId: item.product_id,
          variantId: item.variant_id,
          title: item.title,
          quantity: item.quantity,
          price: parseFloat(item.price) / 100,
          sku: item.sku,
          vendor: item.vendor
        })) || [],
        shippingAddress: checkout.shipping_address ? {
          city: checkout.shipping_address.city,
          province: checkout.shipping_address.province,
          country: checkout.shipping_address.country,
          zip: checkout.shipping_address.zip
        } : null,
        billingAddress: checkout.billing_address ? {
          city: checkout.billing_address.city,
          province: checkout.billing_address.province,
          country: checkout.billing_address.country
        } : null
      };
    }

    // Method 2: Parse from page content
    if (!orderData) {
      const orderNumberEl = document.querySelector('.os-order-number, [data-order-number], .order-number');
      const totalEl = document.querySelector('.payment-due__price, .total-recap__final-price, [data-checkout-total]');

      if (orderNumberEl) {
        orderData = {
          orderNumber: orderNumberEl.textContent?.replace(/[^0-9]/g, ''),
          totalPrice: totalEl?.textContent?.replace(/[^0-9.]/g, '')
        };
      }
    }

    // Method 3: ShopifyAnalytics
    if (!orderData && window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
      const meta = window.ShopifyAnalytics.meta.page;
      if (meta.pageType === 'thankYou') {
        orderData = {
          orderId: meta.resourceId
        };
      }
    }

    if (orderData) {
      trackOrder(orderData);
    }
  }

  // Track completed order
  function trackOrder(orderData) {
    const order = {
      ...orderData,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      source: 'shopify'
    };

    STATE.metrics.Order_Tracking.data.orders.push(order);
    STATE.metrics.Order_Tracking.lastUpdate = Date.now();
    STATE.metrics.Order_Tracking.count = STATE.metrics.Order_Tracking.data.orders.length;

    // Track as conversion event
    trackGeneralEvent('purchase', {
      orderId: order.orderId || order.orderNumber,
      revenue: order.totalPrice,
      currency: order.currency
    });

    // Track as pixel event
    trackPixelEvent('purchase', order.orderId);

    // Update customer details with order info
    if (orderData.email && !STATE.customerDetails.email) {
      updateCustomerDetails({ email: orderData.email });
    }

    if (CONFIG.debug) console.log('Order tracked:', order);

    // Send metrics immediately after purchase
    sendMetrics();
  }

  // =========================================================================
  // CHECKOUT CONTACT INFORMATION METRICS TRACKING
  // =========================================================================

  // Initialize post-contact-info web metrics tracking
  function initCheckoutContactMetrics() {
    const path = window.location.pathname;
    const hostname = window.location.hostname;

    // Only activate on checkout pages
    if (!hostname.includes('checkout.shopify.com') && !path.includes('/checkouts/')) return;

    // Detect and track the current checkout step
    const currentStep = detectCurrentCheckoutStep();
    STATE.checkoutContactInfo.currentStep = currentStep;
    STATE.checkoutContactInfo.stepStartTime = Date.now();

    // Start tracking form field interactions on contact info step
    if (currentStep === 'contact_information') {
      startContactFormFieldTracking();
    }

    // Observe checkout step transitions (Shopify checkout is often SPA-like)
    observeCheckoutStepTransitions();

    if (CONFIG.debug) console.log('Checkout contact metrics initialized, current step:', currentStep);
  }

  // Detect which checkout step the user is currently on
  function detectCurrentCheckoutStep() {
    const path = window.location.pathname;

    if (path.includes('/contact_information') || document.querySelector('[data-step="contact_information"]')) {
      return 'contact_information';
    }
    if (path.includes('/shipping') || document.querySelector('[data-step="shipping_method"]')) {
      return 'shipping_method';
    }
    if (path.includes('/payment') || document.querySelector('[data-step="payment_method"]')) {
      return 'payment_method';
    }
    if (path.includes('/thank_you') || path.includes('/orders/')) {
      return 'order_complete';
    }

    // Shopify Checkout object detection
    if (window.Shopify && window.Shopify.Checkout && window.Shopify.Checkout.step) {
      return window.Shopify.Checkout.step;
    }

    return 'checkout_started';
  }

  // Start tracking form field interactions on the contact info step
  function startContactFormFieldTracking() {
    if (STATE.checkoutContactInfo.formFieldsTracked) return;
    STATE.checkoutContactInfo.formFieldsTracked = true;

    // Contact info form selectors (Shopify checkout variants)
    const fieldSelectors = [
      'input[name*="email"]', 'input[type="email"]',
      'input[name*="first_name"]', 'input[name*="last_name"]',
      'input[name*="address"]', 'input[name*="city"]',
      'input[name*="province"]', 'input[name*="zip"]', 'input[name*="postal"]',
      'input[name*="phone"]', 'input[type="tel"]',
      'input[name*="company"]',
      'select[name*="country"]', 'select[name*="province"]',
      '#checkout_email', '#checkout_shipping_address_first_name',
      '#checkout_shipping_address_last_name', '#checkout_shipping_address_address1',
      '#checkout_shipping_address_address2', '#checkout_shipping_address_city',
      '#checkout_shipping_address_zip', '#checkout_shipping_address_phone',
      '[data-address-field] input', '[data-address-field] select'
    ];

    const allFields = document.querySelectorAll(fieldSelectors.join(', '));

    allFields.forEach(function(field) {
      const fieldName = field.name || field.id || field.getAttribute('data-field') || 'unknown';

      // Initialize field interaction record
      STATE.checkoutContactInfo.fieldInteractions[fieldName] = {
        fieldName: fieldName,
        fieldType: field.type || field.tagName.toLowerCase(),
        focusCount: 0,
        totalFocusTime: 0,
        changeCount: 0,
        firstInteractionTime: null,
        lastInteractionTime: null,
        hasValue: false,
        validationErrors: 0
      };

      // Track focus
      field.addEventListener('focus', function() {
        const now = Date.now();
        STATE.checkoutContactInfo.activeField = fieldName;
        STATE.checkoutContactInfo.activeFieldStartTime = now;
        STATE.checkoutContactInfo.fieldInteractions[fieldName].focusCount++;
        if (!STATE.checkoutContactInfo.fieldInteractions[fieldName].firstInteractionTime) {
          STATE.checkoutContactInfo.fieldInteractions[fieldName].firstInteractionTime = now;
        }
        STATE.checkoutContactInfo.fieldInteractions[fieldName].lastInteractionTime = now;
      });

      // Track blur (calculate focus duration)
      field.addEventListener('blur', function() {
        const now = Date.now();
        if (STATE.checkoutContactInfo.activeField === fieldName && STATE.checkoutContactInfo.activeFieldStartTime) {
          const duration = now - STATE.checkoutContactInfo.activeFieldStartTime;
          STATE.checkoutContactInfo.fieldInteractions[fieldName].totalFocusTime += duration;
        }
        STATE.checkoutContactInfo.fieldInteractions[fieldName].hasValue = !!field.value;
        STATE.checkoutContactInfo.fieldInteractions[fieldName].lastInteractionTime = now;
        STATE.checkoutContactInfo.activeField = null;
        STATE.checkoutContactInfo.activeFieldStartTime = null;
      });

      // Track changes
      field.addEventListener('change', function() {
        STATE.checkoutContactInfo.fieldInteractions[fieldName].changeCount++;
        STATE.checkoutContactInfo.fieldInteractions[fieldName].hasValue = !!field.value;
      });

      // Track validation errors (Shopify adds error classes to fields)
      field.addEventListener('invalid', function() {
        STATE.checkoutContactInfo.fieldInteractions[fieldName].validationErrors++;
      });
    });

    // Also observe for dynamically added error messages
    observeValidationErrors();

    if (CONFIG.debug) console.log('Contact form field tracking started, tracking', allFields.length, 'fields');
  }

  // Observe for validation error messages in checkout forms
  function observeValidationErrors() {
    const errorObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Shopify checkout error selectors
            const isError = node.classList &&
              (node.classList.contains('field__message--error') ||
               node.classList.contains('notice--error') ||
               node.classList.contains('field--error') ||
               node.getAttribute('role') === 'alert');

            if (isError) {
              // Find the associated field
              const parentField = node.closest('.field, .field__input-wrapper, [data-address-field]');
              if (parentField) {
                const input = parentField.querySelector('input, select');
                if (input) {
                  const fieldName = input.name || input.id || 'unknown';
                  if (STATE.checkoutContactInfo.fieldInteractions[fieldName]) {
                    STATE.checkoutContactInfo.fieldInteractions[fieldName].validationErrors++;
                  }
                }
              }
            }
          }
        });
      });
    });

    const checkoutForm = document.querySelector('[data-step="contact_information"], .step__sections, .main__content, #checkout');
    if (checkoutForm) {
      errorObserver.observe(checkoutForm, { childList: true, subtree: true });
    }
  }

  // Observe checkout step transitions (SPA navigation in Shopify checkout)
  function observeCheckoutStepTransitions() {
    // Method 1: MutationObserver for DOM changes indicating step change
    STATE.checkoutContactInfo.stepObserver = new MutationObserver(function(mutations) {
      const newStep = detectCurrentCheckoutStep();

      if (newStep !== STATE.checkoutContactInfo.currentStep) {
        const previousStep = STATE.checkoutContactInfo.currentStep;

        // If transitioning FROM contact_information, capture post-contact metrics
        if (previousStep === 'contact_information') {
          capturePostContactMetrics(newStep);
        }

        // Update current step and reset timer for the new step
        STATE.checkoutContactInfo.currentStep = newStep;
        STATE.checkoutContactInfo.stepStartTime = Date.now();
        STATE.checkoutContactInfo.formFieldsTracked = false;

        // Start field tracking if landing on contact info
        if (newStep === 'contact_information') {
          startContactFormFieldTracking();
        }

        if (CONFIG.debug) console.log('Checkout step transition:', previousStep, '->', newStep);
      }
    });

    const checkoutContainer = document.querySelector('#checkout, .content, [data-step], main, body');
    if (checkoutContainer) {
      STATE.checkoutContactInfo.stepObserver.observe(checkoutContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-step', 'class']
      });
    }

    // Method 2: Listen for Shopify's page:load event (checkout SPA navigations)
    document.addEventListener('page:load', function() {
      const newStep = detectCurrentCheckoutStep();
      if (newStep !== STATE.checkoutContactInfo.currentStep) {
        const previousStep = STATE.checkoutContactInfo.currentStep;

        if (previousStep === 'contact_information') {
          capturePostContactMetrics(newStep);
        }

        STATE.checkoutContactInfo.currentStep = newStep;
        STATE.checkoutContactInfo.stepStartTime = Date.now();
        STATE.checkoutContactInfo.formFieldsTracked = false;

        if (newStep === 'contact_information') {
          startContactFormFieldTracking();
        }
      }
    });

    // Method 3: Listen for URL changes (popstate / hashchange)
    window.addEventListener('popstate', handleCheckoutUrlChange);
    window.addEventListener('hashchange', handleCheckoutUrlChange);

    // Method 4: Intercept history.pushState for SPA step navigation
    const originalPushState = history.pushState;
    history.pushState = function() {
      originalPushState.apply(this, arguments);
      handleCheckoutUrlChange();
    };

    const originalReplaceState = history.replaceState;
    history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      handleCheckoutUrlChange();
    };
  }

  // Handle URL change during checkout
  function handleCheckoutUrlChange() {
    const path = window.location.pathname;
    if (!path.includes('/checkouts/')) return;

    // Small delay to allow DOM update
    setTimeout(function() {
      const newStep = detectCurrentCheckoutStep();
      if (newStep !== STATE.checkoutContactInfo.currentStep) {
        const previousStep = STATE.checkoutContactInfo.currentStep;

        if (previousStep === 'contact_information') {
          capturePostContactMetrics(newStep);
        }

        STATE.checkoutContactInfo.currentStep = newStep;
        STATE.checkoutContactInfo.stepStartTime = Date.now();
        STATE.checkoutContactInfo.formFieldsTracked = false;

        if (newStep === 'contact_information') {
          startContactFormFieldTracking();
        }
      }
    }, 100);
  }

  // Capture comprehensive web tracking metrics after contact info step completion
  function capturePostContactMetrics(nextStep) {
    const now = Date.now();
    const timeOnStep = STATE.checkoutContactInfo.stepStartTime ? now - STATE.checkoutContactInfo.stepStartTime : 0;

    // Collect field interaction summary
    const fieldInteractions = Object.values(STATE.checkoutContactInfo.fieldInteractions);
    const filledFields = fieldInteractions.filter(function(f) { return f.hasValue; });
    const totalValidationErrors = fieldInteractions.reduce(function(sum, f) { return sum + f.validationErrors; }, 0);
    const totalFocusTime = fieldInteractions.reduce(function(sum, f) { return sum + f.totalFocusTime; }, 0);

    // Collect page performance metrics at this point
    var performanceMetrics = null;
    if (window.performance) {
      var navigation = window.performance.getEntriesByType('navigation')[0] || {};
      var paintEntries = window.performance.getEntriesByType('paint') || [];
      var fcp = null;
      for (var i = 0; i < paintEntries.length; i++) {
        if (paintEntries[i].name === 'first-contentful-paint') {
          fcp = paintEntries[i].startTime;
          break;
        }
      }

      performanceMetrics = {
        domContentLoaded: navigation.domContentLoadedEventEnd || null,
        loadComplete: navigation.loadEventEnd || null,
        firstContentfulPaint: fcp,
        resourceCount: window.performance.getEntriesByType('resource').length,
        memoryUsage: window.performance.memory ? {
          usedJSHeapSize: window.performance.memory.usedJSHeapSize,
          totalJSHeapSize: window.performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit
        } : null
      };
    }

    // Collect network metrics snapshot
    var networkInfo = null;
    if (navigator.connection) {
      networkInfo = {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      };
    }

    // Build comprehensive post-contact-info metrics object
    var contactMetrics = {
      step: 'contact_information_completed',
      nextStep: nextStep,
      timestamp: now,
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      cart: STATE.cart,
      stepMetrics: {
        timeOnStep: timeOnStep,
        totalFieldsTracked: fieldInteractions.length,
        fieldsFilled: filledFields.length,
        fieldsCompletionRate: fieldInteractions.length > 0 ? (filledFields.length / fieldInteractions.length * 100).toFixed(1) : 0,
        totalValidationErrors: totalValidationErrors,
        totalFocusTime: totalFocusTime,
        averageTimePerField: filledFields.length > 0 ? Math.round(totalFocusTime / filledFields.length) : 0
      },
      fieldInteractions: fieldInteractions,
      webMetrics: {
        performance: performanceMetrics,
        network: networkInfo,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio
        },
        sessionDuration: now - STATE.pageLoadTime,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: screen.width + 'x' + screen.height,
        referrer: document.referrer
      }
    };

    // Store in Checkout_Contact_Metrics
    STATE.metrics.Checkout_Contact_Metrics.data.steps.push(contactMetrics);
    STATE.metrics.Checkout_Contact_Metrics.data.fieldInteractions.push({
      timestamp: now,
      fields: fieldInteractions
    });
    STATE.metrics.Checkout_Contact_Metrics.data.formCompletions.push({
      timestamp: now,
      timeOnStep: timeOnStep,
      fieldsFilled: filledFields.length,
      totalFields: fieldInteractions.length,
      validationErrors: totalValidationErrors,
      nextStep: nextStep
    });
    STATE.metrics.Checkout_Contact_Metrics.lastUpdate = now;
    STATE.metrics.Checkout_Contact_Metrics.count = STATE.metrics.Checkout_Contact_Metrics.data.steps.length;

    // Also record in Order_Tracking checkout steps for funnel analysis
    trackCheckoutStep('contact_information_completed', {
      timeOnStep: timeOnStep,
      fieldsFilled: filledFields.length,
      totalFields: fieldInteractions.length,
      validationErrors: totalValidationErrors,
      nextStep: nextStep
    });

    // Track as a general event for cross-module visibility
    trackGeneralEvent('checkout_contact_info_completed', {
      timeOnStep: timeOnStep,
      fieldsFilled: filledFields.length,
      totalFields: fieldInteractions.length,
      completionRate: contactMetrics.stepMetrics.fieldsCompletionRate,
      validationErrors: totalValidationErrors
    });

    // Track pixel event for conversion tracking
    trackPixelEvent('checkout_contact_info_completed');

    // Immediately flush metrics to the API (critical checkout funnel data)
    sendMetrics();

    // Reset field interactions for potential re-visit
    STATE.checkoutContactInfo.fieldInteractions = {};

    if (CONFIG.debug) console.log('Post-contact-info metrics captured:', contactMetrics);
  }

  // =========================================================================
  // WISHLIST TRACKING
  // =========================================================================

  // Initialize Wishlist Tracking
  function initWishlistTracking() {
    // Watch for wishlist button clicks
    watchWishlistButtons();

    // Load existing wishlist from storage
    loadWishlistFromStorage();

    if (CONFIG.debug) console.log('Wishlist tracking initialized');
  }

  // Watch wishlist buttons
  function watchWishlistButtons() {
    document.addEventListener('click', function(e) {
      const target = e.target;

      // Common wishlist button selectors
      const wishlistButton = target.closest('.wishlist-btn, .add-to-wishlist, [data-wishlist], .wish-list-button, .favorite-btn, [data-action="add-to-wishlist"]');

      if (wishlistButton) {
        const productElement = wishlistButton.closest('[data-product-id]') || document.querySelector('[data-product-id]');

        const productData = {
          productId: productElement?.getAttribute('data-product-id') || wishlistButton.getAttribute('data-product-id'),
          productName: productElement?.getAttribute('data-product-title') || document.querySelector('.product__title, .product-single__title, h1')?.textContent?.trim(),
          productPrice: productElement?.getAttribute('data-product-price'),
          variantId: wishlistButton.getAttribute('data-variant-id'),
          action: wishlistButton.classList.contains('active') || wishlistButton.getAttribute('data-wishlisted') === 'true' ? 'remove' : 'add'
        };

        trackWishlistEvent(productData.action, productData);
      }
    });
  }

  // Load wishlist from storage
  function loadWishlistFromStorage() {
    try {
      const stored = localStorage.getItem('wishlist') || localStorage.getItem('wt_wishlist');
      if (stored) {
        const wishlist = JSON.parse(stored);
        STATE.metrics.Wishlist_Tracking.data.items = Array.isArray(wishlist) ? wishlist : [];
      }
    } catch (e) {
      // Ignore
    }
  }

  // Track wishlist event
  function trackWishlistEvent(action, productData) {
    const wishlistEvent = {
      action: action,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      product: productData
    };

    STATE.metrics.Wishlist_Tracking.data.events.push(wishlistEvent);
    STATE.metrics.Wishlist_Tracking.lastUpdate = Date.now();
    STATE.metrics.Wishlist_Tracking.count = STATE.metrics.Wishlist_Tracking.data.events.length;

    // Update wishlist items
    if (action === 'add' && productData.productId) {
      const exists = STATE.metrics.Wishlist_Tracking.data.items.find(i => i.productId === productData.productId);
      if (!exists) {
        STATE.metrics.Wishlist_Tracking.data.items.push(productData);
      }
    } else if (action === 'remove' && productData.productId) {
      STATE.metrics.Wishlist_Tracking.data.items = STATE.metrics.Wishlist_Tracking.data.items.filter(i => i.productId !== productData.productId);
    }

    // Track as general event
    trackGeneralEvent('wishlist_' + action, productData);

    if (CONFIG.debug) console.log('Wishlist event tracked:', wishlistEvent);
  }

  // =========================================================================
  // PRODUCT REVIEW TRACKING
  // =========================================================================

  // Initialize Review Tracking
  function initReviewTracking() {
    // Watch for review form submissions
    watchReviewForms();

    // Watch for review interactions
    watchReviewInteractions();

    if (CONFIG.debug) console.log('Review tracking initialized');
  }

  // Watch review form submissions
  function watchReviewForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;

      // Common review form selectors
      if (form.classList.contains('review-form') ||
          form.id?.includes('review') ||
          form.action?.includes('review') ||
          form.querySelector('[name="review[rating]"], [name="rating"], .star-rating')) {

        const ratingInput = form.querySelector('[name="review[rating]"], [name="rating"], input[type="radio"]:checked, .star-rating .active');
        const titleInput = form.querySelector('[name="review[title]"], [name="title"]');
        const bodyInput = form.querySelector('[name="review[body]"], [name="body"], textarea');

        const reviewData = {
          rating: ratingInput?.value || ratingInput?.getAttribute('data-rating'),
          title: titleInput?.value,
          bodyLength: bodyInput?.value?.length || 0,
          productId: form.getAttribute('data-product-id') || document.querySelector('[data-product-id]')?.getAttribute('data-product-id'),
          productName: document.querySelector('.product__title, .product-single__title, h1')?.textContent?.trim()
        };

        trackReviewEvent('submit', reviewData);
      }
    });
  }

  // Watch review interactions
  function watchReviewInteractions() {
    document.addEventListener('click', function(e) {
      const target = e.target;

      // Helpful button clicks
      if (target.closest('.review-helpful, [data-review-helpful], .helpful-btn')) {
        const reviewElement = target.closest('.review, [data-review-id]');
        trackReviewEvent('helpful_click', {
          reviewId: reviewElement?.getAttribute('data-review-id')
        });
      }

      // Read more clicks
      if (target.closest('.review-read-more, .read-more')) {
        trackReviewEvent('read_more_click', {});
      }

      // Star rating clicks (viewing reviews by rating)
      if (target.closest('.rating-filter, [data-rating-filter]')) {
        trackReviewEvent('filter_by_rating', {
          rating: target.getAttribute('data-rating') || target.textContent
        });
      }
    });
  }

  // Track review event
  function trackReviewEvent(action, data) {
    const reviewEvent = {
      action: action,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      data: data
    };

    STATE.metrics.Review_Tracking.data.events.push(reviewEvent);
    STATE.metrics.Review_Tracking.lastUpdate = Date.now();
    STATE.metrics.Review_Tracking.count = STATE.metrics.Review_Tracking.data.events.length;

    if (action === 'submit') {
      STATE.metrics.Review_Tracking.data.reviews.push({
        ...data,
        timestamp: Date.now(),
        visitorId: STATE.visitorId
      });
    }

    // Track as general event
    trackGeneralEvent('review_' + action, data);

    if (CONFIG.debug) console.log('Review event tracked:', reviewEvent);
  }

  // =========================================================================
  // SEARCH TRACKING
  // =========================================================================

  // Initialize Search Tracking
  function initSearchTracking() {
    // Watch search form submissions
    watchSearchForms();

    // Track search from URL
    trackSearchFromUrl();

    if (CONFIG.debug) console.log('Search tracking initialized');
  }

  // Watch search forms
  function watchSearchForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;

      if (form.action?.includes('/search') ||
          form.querySelector('input[name="q"]') ||
          form.classList.contains('search-form')) {

        const searchInput = form.querySelector('input[name="q"], input[type="search"], .search-input');
        if (searchInput && searchInput.value) {
          trackSearchEvent(searchInput.value, 'form_submit');
        }
      }
    });

    // Watch for search input changes (instant search)
    let searchTimeout;
    document.addEventListener('input', function(e) {
      const target = e.target;

      if (target.matches('input[name="q"], input[type="search"], .search-input, [data-search-input]')) {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          if (target.value && target.value.length >= 3) {
            trackSearchEvent(target.value, 'instant_search');
          }
        }, 1000);
      }
    });
  }

  // Track search from URL
  function trackSearchFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q') || urlParams.get('query') || urlParams.get('search');

    if (searchQuery && window.location.pathname.includes('/search')) {
      // Get result count if available
      const resultCountEl = document.querySelector('.results-count, [data-search-results-count], .search-results-count');
      const resultCount = resultCountEl?.textContent?.match(/\d+/)?.[0];

      trackSearchEvent(searchQuery, 'page_load', parseInt(resultCount) || null);
    }
  }

  // Track search event
  function trackSearchEvent(query, source, resultsCount = null) {
    const searchData = {
      query: query,
      source: source,
      resultsCount: resultsCount,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Search_Tracking.data.searches.push(searchData);
    STATE.metrics.Search_Tracking.lastUpdate = Date.now();
    STATE.metrics.Search_Tracking.count = STATE.metrics.Search_Tracking.data.searches.length;

    // Keep only last 50 searches
    if (STATE.metrics.Search_Tracking.data.searches.length > 50) {
      STATE.metrics.Search_Tracking.data.searches.shift();
    }

    // Track as general event
    trackGeneralEvent('search', { query: query, resultsCount: resultsCount });

    if (CONFIG.debug) console.log('Search tracked:', searchData);
  }

  // =========================================================================
  // COUPON/DISCOUNT TRACKING
  // =========================================================================

  // Initialize Coupon Tracking
  function initCouponTracking() {
    // Watch coupon form submissions
    watchCouponForms();

    if (CONFIG.debug) console.log('Coupon tracking initialized');
  }

  // Watch coupon forms
  function watchCouponForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;

      if (form.querySelector('[name="discount"], [name="coupon"], [name="discount_code"]') ||
          form.classList.contains('discount-form') ||
          form.action?.includes('discount')) {

        const couponInput = form.querySelector('[name="discount"], [name="coupon"], [name="discount_code"]');
        if (couponInput && couponInput.value) {
          trackCouponEvent('apply_attempt', couponInput.value);
        }
      }
    });

    // Watch for coupon input changes
    document.addEventListener('click', function(e) {
      const target = e.target;

      // Remove coupon button
      if (target.closest('.remove-discount, [data-remove-discount], .discount-remove')) {
        trackCouponEvent('remove', null);
      }
    });
  }

  // Track coupon event
  function trackCouponEvent(action, code, discountAmount = null) {
    const couponData = {
      action: action,
      code: code,
      discountAmount: discountAmount,
      timestamp: Date.now(),
      page: window.location.pathname,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails(),
      cart: STATE.cart
    };

    STATE.metrics.Coupon_Tracking.data.coupons.push(couponData);
    STATE.metrics.Coupon_Tracking.lastUpdate = Date.now();
    STATE.metrics.Coupon_Tracking.count = STATE.metrics.Coupon_Tracking.data.coupons.length;

    // Track as general event
    trackGeneralEvent('coupon_' + action, { code: code, discountAmount: discountAmount });

    if (CONFIG.debug) console.log('Coupon event tracked:', couponData);
  }

  // =========================================================================
  // HELPER: Track General Event (internal use)
  // =========================================================================

  function trackGeneralEvent(eventName, eventData) {
    if (!STATE.metrics.Event_Tracking.data.events) {
      STATE.metrics.Event_Tracking.data.events = [];
    }

    const event = {
      action: eventName,
      timestamp: Date.now(),
      page: window.location.pathname,
      url: window.location.href,
      data: eventData,
      sessionId: STATE.sessionId,
      visitorId: STATE.visitorId,
      customerDetails: getCustomerDetails()
    };

    STATE.metrics.Event_Tracking.data.events.push(event);
    STATE.metrics.Event_Tracking.lastUpdate = Date.now();
    STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;
  }

  // Send metrics to API
  async function sendMetrics() {
    if (isConsentMissing()) {
      console.warn('[WebTracking] Consent missing — metrics not sent. Set cookie "CK_Cons" or localStorage "cookieConsent" to enable.');
      return;
    }
    if (!CONFIG.domainId) {
      if (CONFIG.debug) console.warn('DomainId not configured');
      return;
    }

    // Update heap tracking before sending
    trackHeapMemory();

    // Update Visitor_ID with customer details
    STATE.metrics.Visitor_ID.data.customerDetails = getCustomerDetails();
    STATE.metrics.Visitor_ID.lastUpdate = Date.now();

    // Update Browser_Fingerprint with customer details
    STATE.metrics.Browser_Fingerprint.data.customerDetails = getCustomerDetails();
    STATE.metrics.Browser_Fingerprint.lastUpdate = Date.now();

    // Update Referrer_Info with customer details
    STATE.metrics.Referrer_Info.data.customerDetails = getCustomerDetails();
    STATE.metrics.Referrer_Info.lastUpdate = Date.now();

    // Update IP_Tracking with customer details
    STATE.metrics.IP_Tracking.data.customerDetails = getCustomerDetails();
    STATE.metrics.IP_Tracking.lastUpdate = Date.now();

    // Update heatmap metadata
    STATE.metrics.Heatmap_Tracking.data.page = window.location.pathname;
    STATE.metrics.Heatmap_Tracking.data.url = window.location.href;
    STATE.metrics.Heatmap_Tracking.data.timestamp = Date.now();
    STATE.metrics.Heatmap_Tracking.data.sessionDuration = Date.now() - STATE.pageLoadTime;
    STATE.metrics.Heatmap_Tracking.data.sessionId = STATE.sessionId;
    STATE.metrics.Heatmap_Tracking.data.visitorId = STATE.visitorId;
    STATE.metrics.Heatmap_Tracking.data.customerDetails = getCustomerDetails();
    STATE.metrics.Heatmap_Tracking.metadata = { pageType: getPageType() };

    // Inject customerDetails into trackingData for API consumption
    STATE.metrics.Customer_Details = {
      name: 'Customer_Details',
      status: 'success',
      data: getCustomerDetails() || {},
      lastUpdate: Date.now(),
      count: STATE.metrics.User_Auth_Tracking.data.signInCaptures?.length || 0
    };

    const payload = {
      domainId: CONFIG.domainId,
      domainName: CONFIG.domainName,
      visitorId: STATE.visitorId,
      sessionId: STATE.sessionId,
      type: 'metrics',
      trackingData: STATE.metrics
    };

    try {
      await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      });

      if (CONFIG.debug) console.log('Metrics sent:', payload);
    } catch (error) {
      if (CONFIG.debug) console.error('Failed to send metrics:', error);

      // Fallback to beacon
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.apiEndpoint, JSON.stringify(payload));
      }
    }
  }

  // Start batch sending
  function startBatchSending() {
    if (STATE.batchTimer) clearInterval(STATE.batchTimer);

    STATE.batchTimer = setInterval(() => {
      sendMetrics();
    }, CONFIG.batchInterval);
  }

  // Event Listeners
  function attachEventListeners() {
    document.addEventListener('click', function(e) {
      trackClick(e);
      // Detect rage clicks and dead clicks for session recording
      detectRageClick(e);
      detectDeadClick(e);
      if (CONFIG.trackProducts) {
        trackProductClick(e);
      }
    });

    if (CONFIG.trackHeatmap) {
      document.addEventListener('mousemove', trackMouseMove);
      document.addEventListener('scroll', trackScroll);
    }

    // Send metrics before page unload
    window.addEventListener('beforeunload', function() {
      sendMetrics();
    });

    // Track visibility changes
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        sendMetrics();
      }
    });
  }

  // Initialize tracking
  function init(config) {
    // Merge config
    Object.assign(CONFIG, config);

    if (!CONFIG.autoTrack) return;
    if (isConsentMissing()) {
      console.warn('[WebTracking] Consent missing — set cookie "CK_Cons" or localStorage "cookieConsent" to enable tracking. Tracking is disabled until consent is provided.');
      return;
    }

    // Initialize IDs
    initVisitorId();
    initSessionId();

    // Initialize Shop Currency (dynamic detection)
    initShopCurrency();

    // Initialize customer details (Shopify integration)
    initCustomerDetails();

    // Gather initial data
    getBrowserFingerprint();
    getReferrerInfo();
    trackIP();

    // Track initial page view
    trackPageView();
    trackPixelEvent('page_view');

    // Attach listeners
    attachEventListeners();

    // Initialize Shopify Cart Tracking
    initShopifyCartTracking();

    // Initialize Page Performance Tracking
    initPagePerformanceTracking();

    // Initialize Error Tracking
    initErrorTracking();

    // Initialize Network Tracking
    initNetworkTracking();

    // Initialize User Auth Tracking
    initUserAuthTracking();

    // Initialize E-commerce Tracking
    initOrderTracking();
    initCheckoutContactMetrics();
    initWishlistTracking();
    initReviewTracking();
    initSearchTracking();
    initCouponTracking();

    // Start batch sending
    startBatchSending();

    if (CONFIG.debug) console.log('Web Tracking SDK initialized', CONFIG);
  }

  // Public API
  window.WebTracking = {
    init: init,
    trackEvent: function(eventName, eventData) {
      // Ensure events array exists
      if (!STATE.metrics.Event_Tracking.data.events) {
        STATE.metrics.Event_Tracking.data.events = [];
      }

      const event = {
        action: eventName,
        timestamp: Date.now(),
        page: window.location.pathname,
        url: window.location.href,
        data: eventData,
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        customerDetails: getCustomerDetails()
      };

      STATE.metrics.Event_Tracking.data.events.push(event);
      STATE.metrics.Event_Tracking.lastUpdate = Date.now();
      STATE.metrics.Event_Tracking.count = STATE.metrics.Event_Tracking.data.events.length;

      if (CONFIG.debug) console.log('Event tracked:', event);
    },
    setCustomer: function(customerData) {
      updateCustomerDetails(customerData);
    },
    getCustomer: function() {
      return getCustomerDetails();
    },
    sendNow: sendMetrics,
    getVisitorId: function() { return STATE.visitorId; },
    getSessionId: function() { return STATE.sessionId; },
    getMetrics: function() { return STATE.metrics; },

    // =========================================================================
    // SHOPIFY CART API
    // =========================================================================

    // Track add to cart manually
    trackAddToCart: function(productData) {
      trackCartEvent('add', {
        productId: productData.productId || productData.id,
        variantId: productData.variantId || productData.variant_id,
        title: productData.title || productData.name,
        price: productData.price,
        quantity: productData.quantity || 1,
        sku: productData.sku,
        vendor: productData.vendor,
        image: productData.image
      });
    },

    // Track cart update manually
    trackCartUpdate: function(cartData) {
      trackCartEvent('update', cartData);
    },

    // Track checkout initiation
    trackCheckout: function(checkoutData) {
      trackCheckout(checkoutData);
    },

    // Get current cart state
    getCart: function() {
      return STATE.cart;
    },

    // Refresh cart data from Shopify
    refreshCart: function() {
      return fetchShopifyCart();
    },

    // Get current currency
    getCurrency: function() {
      return STATE.cart.currency || getShopCurrency();
    },

    // Set currency manually (override auto-detection)
    setCurrency: function(currencyCode) {
      STATE.cart.currency = currencyCode;
      if (CONFIG.debug) console.log('Currency set to:', currencyCode);
    },

    // =========================================================================
    // ERROR TRACKING API
    // =========================================================================

    // Track error manually
    trackError: function(errorData) {
      trackError({
        type: errorData.type || 'custom_error',
        message: errorData.message,
        stack: errorData.stack,
        context: errorData.context,
        severity: errorData.severity || 'error'
      });
    },

    // Get all tracked errors
    getErrors: function() {
      return STATE.metrics.Error_Tracking.data.errors;
    },

    // =========================================================================
    // PERFORMANCE API
    // =========================================================================

    // Get page performance data
    getPerformance: function() {
      return STATE.metrics.Page_Performance.data;
    },

    // Track custom performance mark
    trackPerformanceMark: function(markName) {
      if (window.performance && window.performance.mark) {
        window.performance.mark(markName);
        trackGeneralEvent('performance_mark', { mark: markName, timestamp: Date.now() });
      }
    },

    // =========================================================================
    // NETWORK TRACKING API
    // =========================================================================

    // Get network requests
    getNetworkRequests: function() {
      return STATE.metrics.Network_Tracking.data.requests;
    },

    // Get failed requests
    getFailedRequests: function() {
      return STATE.metrics.Network_Tracking.data.failedRequests;
    },

    // =========================================================================
    // USER AUTH / SIGN-IN API
    // =========================================================================

    // Track login with full identity capture
    trackLogin: function(method, customerData) {
      trackAuthEvent('login', { method: method || 'manual' });
      if (customerData) {
        captureSignInIdentity({
          email: customerData.email,
          firstName: customerData.firstName || customerData.first_name,
          lastName: customerData.lastName || customerData.last_name,
          phone: customerData.phone || customerData.mobile,
          customerId: customerData.id || customerData.customerId,
          method: method || 'manual',
          source: 'api_trackLogin'
        });
      }
    },

    // Track logout
    trackLogout: function() {
      trackAuthEvent('logout', {});
      STATE.metrics.User_Auth_Tracking.data.currentSignInState = {
        isLoggedIn: false,
        loggedOutAt: Date.now(),
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId
      };
      // Clear customer details
      STATE.customerDetails = {
        email: null,
        mobile: null,
        name: null,
        firstName: null,
        lastName: null,
        customerId: null,
        address: null,
        city: null,
        country: null,
        ordersCount: null,
        totalSpent: null,
        tags: null,
        acceptsMarketing: null
      };
      localStorage.removeItem('wt_customer_details');
      sessionStorage.removeItem('wt_signin_tracked');
      sendMetrics();
    },

    // Track signup with identity capture
    trackSignup: function(method, customerData) {
      trackAuthEvent('signup', { method: method || 'manual' });
      if (customerData) {
        captureSignInIdentity({
          email: customerData.email,
          firstName: customerData.firstName || customerData.first_name,
          lastName: customerData.lastName || customerData.last_name,
          phone: customerData.phone || customerData.mobile,
          customerId: customerData.id || customerData.customerId,
          method: 'registration',
          source: 'api_trackSignup'
        });
      }
    },

    // Identify user (set customer details and capture sign-in identity)
    identify: function(userData) {
      updateCustomerDetails({
        email: userData.email,
        mobile: userData.phone || userData.mobile,
        firstName: userData.firstName || userData.first_name,
        lastName: userData.lastName || userData.last_name,
        name: userData.name,
        customerId: userData.id || userData.customerId,
        address: userData.address,
        city: userData.city,
        country: userData.country,
        ordersCount: userData.ordersCount || userData.orders_count,
        totalSpent: userData.totalSpent || userData.total_spent,
        tags: userData.tags,
        acceptsMarketing: userData.acceptsMarketing || userData.accepts_marketing
      });
      captureSignInIdentity({
        email: userData.email,
        firstName: userData.firstName || userData.first_name,
        lastName: userData.lastName || userData.last_name,
        phone: userData.phone || userData.mobile,
        customerId: userData.id || userData.customerId,
        ordersCount: userData.ordersCount || userData.orders_count,
        totalSpent: userData.totalSpent || userData.total_spent,
        tags: userData.tags,
        acceptsMarketing: userData.acceptsMarketing || userData.accepts_marketing,
        method: 'manual',
        source: 'api_identify'
      });
    },

    // Get auth events
    getAuthEvents: function() {
      return STATE.metrics.User_Auth_Tracking.data.events;
    },

    // Get all sign-in identity captures
    getSignInCaptures: function() {
      return STATE.metrics.User_Auth_Tracking.data.signInCaptures;
    },

    // Get current sign-in state
    getSignInState: function() {
      return STATE.metrics.User_Auth_Tracking.data.currentSignInState;
    },

    // =========================================================================
    // ORDER/PURCHASE API (E-commerce)
    // =========================================================================

    // Track purchase/order manually
    trackPurchase: function(orderData) {
      trackOrder({
        orderId: orderData.orderId || orderData.order_id,
        orderNumber: orderData.orderNumber || orderData.order_number,
        email: orderData.email,
        currency: orderData.currency || 'USD',
        subtotalPrice: orderData.subtotal,
        totalTax: orderData.tax,
        totalPrice: orderData.total || orderData.revenue,
        totalShipping: orderData.shipping,
        totalDiscount: orderData.discount,
        lineItems: orderData.items || orderData.products,
        paymentMethod: orderData.paymentMethod,
        shippingMethod: orderData.shippingMethod
      });
    },

    // Track checkout step manually
    trackCheckoutStep: function(step, data) {
      trackCheckoutStep(step, data || {});
    },

    // Get all orders
    getOrders: function() {
      return STATE.metrics.Order_Tracking.data.orders;
    },

    // Get checkout funnel steps
    getCheckoutSteps: function() {
      return STATE.metrics.Order_Tracking.data.checkoutSteps;
    },

    // Get post-contact-info web tracking metrics
    getContactMetrics: function() {
      return STATE.metrics.Checkout_Contact_Metrics.data;
    },

    // Get current checkout step
    getCurrentCheckoutStep: function() {
      return STATE.checkoutContactInfo.currentStep;
    },

    // Get field interaction data for the current contact info step
    getContactFieldInteractions: function() {
      return STATE.checkoutContactInfo.fieldInteractions;
    },

    // =========================================================================
    // WISHLIST API
    // =========================================================================

    // Track add to wishlist
    trackWishlistAdd: function(product) {
      trackWishlistEvent('add', {
        productId: product.productId || product.id,
        productName: product.productName || product.title || product.name,
        productPrice: product.price,
        variantId: product.variantId
      });
    },

    // Track remove from wishlist
    trackWishlistRemove: function(product) {
      trackWishlistEvent('remove', {
        productId: product.productId || product.id,
        productName: product.productName || product.title || product.name
      });
    },

    // Get wishlist items
    getWishlist: function() {
      return STATE.metrics.Wishlist_Tracking.data.items;
    },

    // =========================================================================
    // REVIEW API
    // =========================================================================

    // Track review submission
    trackReviewSubmit: function(reviewData) {
      trackReviewEvent('submit', {
        rating: reviewData.rating,
        title: reviewData.title,
        body: reviewData.body,
        productId: reviewData.productId,
        productName: reviewData.productName
      });
    },

    // Track review view
    trackReviewView: function(productId) {
      trackReviewEvent('view', { productId: productId });
    },

    // Get reviews
    getReviews: function() {
      return STATE.metrics.Review_Tracking.data.reviews;
    },

    // =========================================================================
    // SEARCH API
    // =========================================================================

    // Track search
    trackSearch: function(query, resultsCount) {
      trackSearchEvent(query, 'manual', resultsCount);
    },

    // Get search history
    getSearchHistory: function() {
      return STATE.metrics.Search_Tracking.data.searches;
    },

    // =========================================================================
    // COUPON API
    // =========================================================================

    // Track coupon applied
    trackCouponApply: function(code, discountAmount) {
      trackCouponEvent('apply', code, discountAmount);
    },

    // Track coupon success
    trackCouponSuccess: function(code, discountAmount) {
      trackCouponEvent('apply_success', code, discountAmount);
    },

    // Track coupon failure
    trackCouponFailed: function(code, reason) {
      trackCouponEvent('apply_failed', code);
      trackGeneralEvent('coupon_failed', { code: code, reason: reason });
    },

    // Track coupon removed
    trackCouponRemove: function(code) {
      trackCouponEvent('remove', code);
    },

    // Get coupon history
    getCoupons: function() {
      return STATE.metrics.Coupon_Tracking.data.coupons;
    },

    // =========================================================================
    // UTILITY API
    // =========================================================================

    // Enable debug mode
    debug: function(enabled) {
      CONFIG.debug = enabled !== false;
      console.log('Debug mode:', CONFIG.debug ? 'enabled' : 'disabled');
    },

    // Get all tracking data summary
    getSummary: function() {
      return {
        visitorId: STATE.visitorId,
        sessionId: STATE.sessionId,
        customer: getCustomerDetails(),
        cart: STATE.cart,
        pageViews: STATE.metrics.Event_Tracking.data.events?.filter(e => e.action === 'page_view').length || 0,
        productClicks: STATE.metrics.Product_Click_Tracking.count,
        cartEvents: STATE.metrics.Cart_Tracking.count,
        orders: STATE.metrics.Order_Tracking.count,
        checkoutSteps: STATE.metrics.Order_Tracking.data.checkoutSteps?.length || 0,
        contactInfoCompletions: STATE.metrics.Checkout_Contact_Metrics.data.formCompletions?.length || 0,
        wishlistItems: STATE.metrics.Wishlist_Tracking.data.items?.length || 0,
        reviews: STATE.metrics.Review_Tracking.count,
        searches: STATE.metrics.Search_Tracking.count,
        couponsUsed: STATE.metrics.Coupon_Tracking.count,
        signInCaptures: STATE.metrics.User_Auth_Tracking.data.signInCaptures?.length || 0,
        isSignedIn: !!(STATE.metrics.User_Auth_Tracking.data.currentSignInState && STATE.metrics.User_Auth_Tracking.data.currentSignInState.isLoggedIn),
        errors: STATE.metrics.Error_Tracking.count,
        performance: STATE.metrics.Page_Performance.data.performanceGrade,
        sessionDuration: Date.now() - STATE.pageLoadTime
      };
    },
  };

  // Auto-initialize
  function autoInit() {
    if (window.WebTrackingConfig) {
      init(window.WebTrackingConfig);
    } else {
      // Initialize with default CONFIG when no explicit config is provided
      init({});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

})(window, document);
